import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { httpsCallable } from "firebase/functions";
import fixWebmDuration from "fix-webm-duration";
import { applyPalette, GIFEncoder, quantize } from "gifenc";
import { ref as storageRef, uploadBytes } from "firebase/storage";
import "./AdminStratsPlannerPage.css";
import { getCroppedImg } from "../utils/cropImage";
import { functions as firebaseFunctions, storage } from "../Security/firebase-config";
import { useAuth } from "../context/AuthContext";
import { FUNCTION_NAMES } from "../utils/functionNames";
import useGuideMediaManifestVersion from "../hooks/useGuideMediaManifestVersion";
import {
  ensureGuideMediaManifestLoaded,
  getGuideMediaAsset,
} from "../utils/guideMediaManifest";
import {
  getGuidePublishRoutes,
  getGuidePublishSections,
  getGuidePublishSlotById,
  getGuidePublishSlotsForSection,
} from "../utils/guidePublishSlots";
import {
  dataUrlToBlob,
  deleteStratsPlannerMedia,
  loadSharedStratsPlannerMediaLibrary,
  STRATS_PLANNER_SHARED_LIBRARY_SCOPE,
  uploadStratsPlannerMedia,
} from "../utils/stratsPlannerMedia";

const PASSWORD_HASH =
  "820db201216a8dd362f413d229ed47aeb3bb4608b52036f0d42906857815704a";
const UNLOCK_SESSION_KEY = "valovate:strats-planner:unlocked";
const DB_NAME = "valovate-strats-planner";
const DB_VERSION = 3;
const WORKSPACE_KEY = "current-workspace";
const MEDIA_LIBRARY_KEY = "planner-media-library";
const FIXED_CANVAS_SIZE = 3000;
const DEFAULT_FRAME_HOLD_MS = 900;
const DEFAULT_TRANSITION_MS = 550;
const DEFAULT_FPS = 12;
const DEFAULT_PLAYBACK_RATE = 1;
const DEFAULT_FILE_BASENAME = "valovate-strats-planner";
const GUIDE_PUBLISH_STAGING_PREFIX = "strats-planner-guide-publish";
const GIF_MAX_COLORS = 256;
const GIF_COLOR_FORMAT = "rgb565";
const EMPTY_MAP_NAMES = [
  "Abyss",
  "Ascent",
  "Bind",
  "Breeze",
  "Corrode",
  "Fracture",
  "Haven",
  "Icebox",
  "Lotus",
  "Pearl",
  "Split",
  "Summit",
  "Sunset",
];

const STARTER_ASSET_COUNT = 24;
const PLANNER_DB_REQUIRED_STORES = ["workspace", "savedProjects", "mediaLibrary"];

function uid(prefix) {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`;
}

function cloneValue(value) {
  if (typeof structuredClone === "function") {
    return structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value));
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function lerp(start, end, amount) {
  return start + (end - start) * amount;
}

function normalizeAngle(value) {
  const angle = Number.isFinite(value) ? value : 0;
  const normalized = angle % 360;
  return normalized < 0 ? normalized + 360 : normalized;
}

function sanitizeNumber(value, fallback) {
  const next = Number(value);
  return Number.isFinite(next) ? next : fallback;
}

function sanitizeFileName(value) {
  return String(value || DEFAULT_FILE_BASENAME)
    .trim()
    .replace(/[^\w.-]+/g, "-")
    .replace(/^-+|-+$/g, "") || DEFAULT_FILE_BASENAME;
}

function getAnimationExportPlan(project) {
  const fps = clamp(
    Math.round(sanitizeNumber(project.exportSettings?.fps, DEFAULT_FPS)),
    1,
    60
  );
  const playbackRate = clamp(
    sanitizeNumber(project.exportSettings?.playbackRate, DEFAULT_PLAYBACK_RATE),
    0.1,
    4
  );
  const timelineDurationMs = Math.max(0, getTimelineDuration(project));
  const outputFrameIntervalMs = 1000 / fps;
  const timelineFrameStepMs = outputFrameIntervalMs * playbackRate;
  const outputDurationMs = timelineDurationMs / playbackRate;
  const totalFrames = Math.max(1, Math.ceil(timelineDurationMs / timelineFrameStepMs));

  return {
    fps,
    playbackRate,
    timelineDurationMs,
    outputFrameIntervalMs,
    timelineFrameStepMs,
    outputDurationMs,
    totalFrames,
  };
}

function buildGifFrameDelayList(totalFrames, outputDurationMs) {
  const totalCentiseconds = Math.max(totalFrames, Math.max(1, Math.round(outputDurationMs / 10)));
  let previousBoundary = 0;

  return Array.from({ length: totalFrames }, (_, index) => {
    const nextBoundary = Math.max(
      previousBoundary + 1,
      Math.round(((index + 1) * totalCentiseconds) / totalFrames)
    );
    const delayMs = (nextBoundary - previousBoundary) * 10;
    previousBoundary = nextBoundary;
    return delayMs;
  });
}

function buildGuidePublishStagingStoragePath(ownerUid, fileBaseName) {
  const nextFileBaseName = sanitizeFileName(fileBaseName || DEFAULT_FILE_BASENAME);
  const publishJobId = uid("guide-publish");
  return `${GUIDE_PUBLISH_STAGING_PREFIX}/${ownerUid}/${publishJobId}/${nextFileBaseName}.webm`;
}

function isLegacyPlannerBuiltinSource(value) {
  return typeof value === "string" && value.startsWith("/assets/");
}

function normalizePlannerMediaSource(value) {
  return isLegacyPlannerBuiltinSource(value) ? "" : String(value || "");
}

function normalizePlannerMediaOwnerMetadata(value, source) {
  return isLegacyPlannerBuiltinSource(source) ? null : value || null;
}

function moveArrayItem(items, fromIndex, toIndex) {
  if (
    !Array.isArray(items) ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= items.length ||
    toIndex >= items.length ||
    fromIndex === toIndex
  ) {
    return items;
  }

  const next = [...items];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
}

function confirmMenuAction(message) {
  if (typeof window === "undefined" || typeof window.confirm !== "function") {
    return true;
  }
  return window.confirm(message);
}

function slugify(value, fallback = "item") {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return normalized || fallback;
}

function buildMapLibraryKey(name, index) {
  return `map-${slugify(name || `map-${index + 1}`)}`;
}

function buildStarterAssetLibraryKey(index) {
  return `starter-asset-${String(index + 1).padStart(2, "0")}`;
}

function createMapItem({
  id,
  libraryKey,
  name,
  src = "",
  storagePath = null,
  ownerUid = null,
}) {
  return {
    id: id || uid("map"),
    libraryKey,
    name,
    src,
    storagePath,
    ownerUid,
  };
}

function createSubAssetItem({
  id,
  libraryKey,
  name,
  src = "",
  storagePath = null,
  ownerUid = null,
}) {
  return {
    id: id || uid("sub-asset"),
    libraryKey,
    name,
    src,
    storagePath,
    ownerUid,
  };
}

function createAssetItem({
  id,
  libraryKey,
  name,
  src = "",
  storagePath = null,
  ownerUid = null,
  subAssets = [],
}) {
  return {
    id: id || uid("asset"),
    libraryKey,
    name,
    src,
    storagePath,
    ownerUid,
    subAssets,
  };
}

function findSubAssetById(assetItem, subAssetId) {
  if (!assetItem || !subAssetId || !Array.isArray(assetItem.subAssets)) {
    return null;
  }

  return assetItem.subAssets.find((entry) => entry.id === subAssetId) || null;
}

function getSequenceItemMedia(assetLookup, item) {
  const assetItem = assetLookup.get(item.assetId);
  if (!assetItem) {
    return null;
  }

  const abilityItem = item.subAssetId ? findSubAssetById(assetItem, item.subAssetId) : null;
  if (item.subAssetId) {
    return {
      assetItem,
      abilityItem,
      src: abilityItem?.src || "",
      label: abilityItem?.name || assetItem.name || "Unnamed image",
    };
  }

  return {
    assetItem,
    abilityItem: null,
    src: assetItem.src || "",
    label: assetItem.name || "Unnamed image",
  };
}

function createEmptyMediaLibrary() {
  return {
    maps: [],
    assets: [],
  };
}

function sequenceLabel(index) {
  return String(index + 1);
}

function createEmptySequence(index) {
  return {
    id: `sequence-${index + 1}`,
    name: `Sequence ${index + 1}`,
    items: [],
    edited: false,
  };
}

function createStarterAssets() {
  return Array.from({ length: STARTER_ASSET_COUNT }, (_, index) => {
    return createAssetItem({
      id: uid("asset"),
      libraryKey: buildStarterAssetLibraryKey(index),
      name: `Main Image ${index + 1}`,
      src: "",
      subAssets: [],
    });
  });
}

function createStarterMaps() {
  return EMPTY_MAP_NAMES.map((name, index) =>
    createMapItem({
      id: uid("map"),
      libraryKey: buildMapLibraryKey(name, index),
      name,
      src: "",
    })
  );
}

function createInitialProject() {
  return {
    version: 1,
    projectName: "Untitled Strats Planner",
    updatedAt: Date.now(),
    backgroundMapId: null,
    canvas: {
      width: FIXED_CANVAS_SIZE,
      height: FIXED_CANVAS_SIZE,
    },
    exportSettings: {
      exportMode: "video",
      videoContainer: "webm",
      fps: DEFAULT_FPS,
      frameHoldMs: DEFAULT_FRAME_HOLD_MS,
      transitionMs: DEFAULT_TRANSITION_MS,
      playbackRate: DEFAULT_PLAYBACK_RATE,
      fileBaseName: DEFAULT_FILE_BASENAME,
    },
    maps: createStarterMaps(),
    assets: createStarterAssets(),
    sequences: Array.from({ length: 12 }, (_, index) => createEmptySequence(index)),
  };
}

function normalizeProject(input) {
  const base = createInitialProject();
  const project = input ? cloneValue(input) : base;
  const next = {
    ...base,
    ...project,
    canvas: {
      ...base.canvas,
      ...(project.canvas || {}),
    },
    exportSettings: {
      ...base.exportSettings,
      ...(project.exportSettings || {}),
    },
    maps: Array.isArray(project.maps) ? project.maps : base.maps,
    assets: Array.isArray(project.assets) ? project.assets : base.assets,
    sequences: Array.isArray(project.sequences) ? project.sequences : base.sequences,
  };
  const sourceCanvasWidth = Math.max(1, sanitizeNumber(project.canvas?.width, FIXED_CANVAS_SIZE));
  const sourceCanvasHeight = Math.max(1, sanitizeNumber(project.canvas?.height, FIXED_CANVAS_SIZE));
  const positionScaleX = FIXED_CANVAS_SIZE / sourceCanvasWidth;
  const positionScaleY = FIXED_CANVAS_SIZE / sourceCanvasHeight;
  const shouldScaleCanvasItems =
    sourceCanvasWidth !== FIXED_CANVAS_SIZE || sourceCanvasHeight !== FIXED_CANVAS_SIZE;

  next.canvas.width = FIXED_CANVAS_SIZE;
  next.canvas.height = FIXED_CANVAS_SIZE;
  next.exportSettings.fps = clamp(
    Math.round(sanitizeNumber(next.exportSettings.fps, DEFAULT_FPS)),
    1,
    60
  );
  next.exportSettings.frameHoldMs = clamp(
    Math.round(sanitizeNumber(next.exportSettings.frameHoldMs, DEFAULT_FRAME_HOLD_MS)),
    100,
    60000
  );
  next.exportSettings.transitionMs = clamp(
    Math.round(sanitizeNumber(next.exportSettings.transitionMs, DEFAULT_TRANSITION_MS)),
    0,
    60000
  );
  next.exportSettings.playbackRate = clamp(
    sanitizeNumber(next.exportSettings.playbackRate, DEFAULT_PLAYBACK_RATE),
    0.1,
    4
  );
  next.exportSettings.videoContainer = "webm";
  next.exportSettings.exportMode = ["video", "gif", "png", "json"].includes(
    next.exportSettings.exportMode
  )
    ? next.exportSettings.exportMode
    : "video";
  next.exportSettings.fileBaseName = sanitizeFileName(next.exportSettings.fileBaseName);
  next.projectName = String(next.projectName || base.projectName);

  const legacyBackgroundMapId =
    project.backgroundMapId ||
    (Array.isArray(next.sequences)
      ? next.sequences.find((sequence) => sequence?.backgroundMapId)?.backgroundMapId || null
      : null);

  next.maps = next.maps.map((mapItem, index) =>
    createMapItem({
      id: mapItem.id || uid("map"),
      libraryKey: mapItem.libraryKey || buildMapLibraryKey(mapItem.name || EMPTY_MAP_NAMES[index], index),
      name: mapItem.name || "Untitled Map",
      src: normalizePlannerMediaSource(mapItem.src),
      storagePath: normalizePlannerMediaOwnerMetadata(mapItem.storagePath, mapItem.src),
      ownerUid: normalizePlannerMediaOwnerMetadata(mapItem.ownerUid, mapItem.src),
    })
  );
  next.backgroundMapId = legacyBackgroundMapId || null;

  next.assets = next.assets.map((assetItem, index) =>
    createAssetItem({
      id: assetItem.id || uid("asset"),
      libraryKey: assetItem.libraryKey || buildStarterAssetLibraryKey(index),
      name: assetItem.name || `Main Image ${index + 1}`,
      src: normalizePlannerMediaSource(assetItem.src),
      storagePath: normalizePlannerMediaOwnerMetadata(assetItem.storagePath, assetItem.src),
      ownerUid: normalizePlannerMediaOwnerMetadata(assetItem.ownerUid, assetItem.src),
      subAssets: Array.isArray(assetItem.subAssets)
        ? assetItem.subAssets.map((subAsset, subIndex) =>
            createSubAssetItem({
              id: subAsset.id || uid("sub-asset"),
              libraryKey:
                subAsset.libraryKey ||
                `${assetItem.libraryKey || buildStarterAssetLibraryKey(index)}-variant-${
                  subIndex + 1
                }`,
              name: subAsset.name || `Variant ${subIndex + 1}`,
              src: normalizePlannerMediaSource(subAsset.src),
              storagePath: normalizePlannerMediaOwnerMetadata(subAsset.storagePath, subAsset.src),
              ownerUid: normalizePlannerMediaOwnerMetadata(subAsset.ownerUid, subAsset.src),
            })
          )
        : [],
    })
  );

  const normalizedSequences = Array.from({ length: 12 }, (_, index) => {
    const source = next.sequences[index] || createEmptySequence(index);
    return {
      id: source.id || `sequence-${index + 1}`,
      name: source.name || `Sequence ${index + 1}`,
      edited:
        typeof source.edited === "boolean"
          ? source.edited
          : Boolean(source.backgroundMapId || (Array.isArray(source.items) && source.items.length > 0)),
      items: Array.isArray(source.items)
        ? source.items.map((item, itemIndex) => ({
            instanceId: item.instanceId || uid(`item-${index + 1}-${itemIndex + 1}`),
            assetId: item.assetId || "",
            subAssetId: item.subAssetId || null,
            x: sanitizeNumber(item.x, 120) * (shouldScaleCanvasItems ? positionScaleX : 1),
            y: sanitizeNumber(item.y, 120) * (shouldScaleCanvasItems ? positionScaleY : 1),
            width: Math.max(
              20,
              sanitizeNumber(item.width, 140) * (shouldScaleCanvasItems ? positionScaleX : 1)
            ),
            height: Math.max(
              20,
              sanitizeNumber(item.height, 140) * (shouldScaleCanvasItems ? positionScaleY : 1)
            ),
            rotation: normalizeAngle(sanitizeNumber(item.rotation, 0)),
            flipX: Boolean(item.flipX),
            flipY: Boolean(item.flipY),
            zIndex: sanitizeNumber(item.zIndex, itemIndex + 1),
            opacity: clamp(sanitizeNumber(item.opacity, 1), 0, 1),
          }))
        : [],
    };
  });

  next.sequences = normalizedSequences;
  return next;
}

function buildMediaLibrarySnapshot(project) {
  return {
    maps: (project.maps || []).map((mapItem) => ({
      libraryKey: mapItem.libraryKey || buildMapLibraryKey(mapItem.name, 0),
      name: mapItem.name || "Untitled Map",
      src: normalizePlannerMediaSource(mapItem.src),
      storagePath: normalizePlannerMediaOwnerMetadata(mapItem.storagePath, mapItem.src),
      ownerUid: normalizePlannerMediaOwnerMetadata(mapItem.ownerUid, mapItem.src),
    })),
    assets: (project.assets || []).map((assetItem, assetIndex) => ({
      libraryKey: assetItem.libraryKey || buildStarterAssetLibraryKey(assetIndex),
      name: assetItem.name || `Main Image ${assetIndex + 1}`,
      src: normalizePlannerMediaSource(assetItem.src),
      storagePath: normalizePlannerMediaOwnerMetadata(assetItem.storagePath, assetItem.src),
      ownerUid: normalizePlannerMediaOwnerMetadata(assetItem.ownerUid, assetItem.src),
      subAssets: Array.isArray(assetItem.subAssets)
        ? assetItem.subAssets.map((subAsset, subIndex) => ({
            libraryKey:
              subAsset.libraryKey ||
              `${assetItem.libraryKey || buildStarterAssetLibraryKey(assetIndex)}-variant-${
                subIndex + 1
              }`,
            name: subAsset.name || `Variant ${subIndex + 1}`,
            src: normalizePlannerMediaSource(subAsset.src),
            storagePath: normalizePlannerMediaOwnerMetadata(subAsset.storagePath, subAsset.src),
            ownerUid: normalizePlannerMediaOwnerMetadata(subAsset.ownerUid, subAsset.src),
          }))
        : [],
    })),
  };
}

function normalizeMediaLibrary(input) {
  const rawLibrary =
    input && typeof input === "object" && !Array.isArray(input) ? input : createEmptyMediaLibrary();

  return {
    maps: Array.isArray(rawLibrary.maps)
      ? rawLibrary.maps.map((mapItem, index) => ({
          libraryKey:
            mapItem.libraryKey || buildMapLibraryKey(mapItem.name || EMPTY_MAP_NAMES[index], index),
          name: mapItem.name || "Untitled Map",
          src: normalizePlannerMediaSource(mapItem.src),
          storagePath: normalizePlannerMediaOwnerMetadata(mapItem.storagePath, mapItem.src),
          ownerUid: normalizePlannerMediaOwnerMetadata(mapItem.ownerUid, mapItem.src),
        }))
      : [],
    assets: Array.isArray(rawLibrary.assets)
      ? rawLibrary.assets.map((assetItem, assetIndex) => ({
          libraryKey: assetItem.libraryKey || buildStarterAssetLibraryKey(assetIndex),
          name: assetItem.name || `Main Image ${assetIndex + 1}`,
          src: normalizePlannerMediaSource(assetItem.src),
          storagePath: normalizePlannerMediaOwnerMetadata(assetItem.storagePath, assetItem.src),
          ownerUid: normalizePlannerMediaOwnerMetadata(assetItem.ownerUid, assetItem.src),
          subAssets: Array.isArray(assetItem.subAssets)
            ? assetItem.subAssets.map((subAsset, subIndex) => ({
                libraryKey:
                  subAsset.libraryKey ||
                  `${assetItem.libraryKey || buildStarterAssetLibraryKey(assetIndex)}-variant-${
                    subIndex + 1
                  }`,
                name: subAsset.name || `Variant ${subIndex + 1}`,
                src: normalizePlannerMediaSource(subAsset.src),
                storagePath: normalizePlannerMediaOwnerMetadata(subAsset.storagePath, subAsset.src),
                ownerUid: normalizePlannerMediaOwnerMetadata(subAsset.ownerUid, subAsset.src),
              }))
            : [],
        }))
      : [],
  };
}

function mergeMediaLibraries(primaryInput, secondaryInput) {
  const primary = normalizeMediaLibrary(primaryInput);
  const secondary = normalizeMediaLibrary(secondaryInput);
  const secondaryMapsByLibraryKey = new Map(
    secondary.maps.map((mapItem, index) => [
      mapItem.libraryKey || buildMapLibraryKey(mapItem.name || EMPTY_MAP_NAMES[index], index),
      mapItem,
    ])
  );
  const mergedMaps = [];

  for (const primaryMap of primary.maps) {
    const fallbackMap = secondaryMapsByLibraryKey.get(primaryMap.libraryKey);
    mergedMaps.push({
      libraryKey: primaryMap.libraryKey,
      name: primaryMap.name || fallbackMap?.name || "Untitled Map",
      src: primaryMap.src || fallbackMap?.src || "",
      storagePath: primaryMap.storagePath || fallbackMap?.storagePath || null,
      ownerUid: primaryMap.ownerUid || fallbackMap?.ownerUid || null,
    });
    secondaryMapsByLibraryKey.delete(primaryMap.libraryKey);
  }

  for (const [, fallbackMap] of secondaryMapsByLibraryKey.entries()) {
    mergedMaps.push({ ...fallbackMap });
  }

  const secondaryAssetsByLibraryKey = new Map(
    secondary.assets.map((assetItem, assetIndex) => [
      assetItem.libraryKey || buildStarterAssetLibraryKey(assetIndex),
      assetItem,
    ])
  );
  const mergedAssets = [];

  for (const primaryAsset of primary.assets) {
    const fallbackAsset = secondaryAssetsByLibraryKey.get(primaryAsset.libraryKey);
    mergedAssets.push({
      libraryKey: primaryAsset.libraryKey,
      name: primaryAsset.name || fallbackAsset?.name || "Untitled Asset",
      src: primaryAsset.src || fallbackAsset?.src || "",
      storagePath: primaryAsset.storagePath || fallbackAsset?.storagePath || null,
      ownerUid: primaryAsset.ownerUid || fallbackAsset?.ownerUid || null,
      subAssets: mergeSubAssetsWithLibrary(
        fallbackAsset?.subAssets || [],
        primaryAsset.subAssets || [],
        primaryAsset.libraryKey
      ).map((subAsset) => ({
        libraryKey: subAsset.libraryKey,
        name: subAsset.name || "Untitled Variant",
        src: subAsset.src || "",
        storagePath: subAsset.storagePath || null,
        ownerUid: subAsset.ownerUid || null,
      })),
    });
    secondaryAssetsByLibraryKey.delete(primaryAsset.libraryKey);
  }

  for (const [, fallbackAsset] of secondaryAssetsByLibraryKey.entries()) {
    mergedAssets.push({ ...fallbackAsset });
  }

  return {
    maps: mergedMaps,
    assets: mergedAssets,
  };
}

function mergeSubAssetsWithLibrary(localSubAssets, librarySubAssets, parentLibraryKey) {
  const localByLibraryKey = new Map(
    (localSubAssets || []).map((subAsset, index) => [
      subAsset.libraryKey || `${parentLibraryKey}-variant-${index + 1}`,
      subAsset,
    ])
  );
  const mergedSubAssets = [];

  for (const librarySubAsset of librarySubAssets || []) {
    const existingSubAsset = localByLibraryKey.get(librarySubAsset.libraryKey);
    if (existingSubAsset) {
      mergedSubAssets.push(
        createSubAssetItem({
          ...existingSubAsset,
          libraryKey: librarySubAsset.libraryKey,
          name: librarySubAsset.name || existingSubAsset.name,
          src: librarySubAsset.src || existingSubAsset.src || "",
          storagePath: librarySubAsset.storagePath || existingSubAsset.storagePath || null,
          ownerUid: librarySubAsset.ownerUid || existingSubAsset.ownerUid || null,
        })
      );
      localByLibraryKey.delete(librarySubAsset.libraryKey);
    } else {
      mergedSubAssets.push(
        createSubAssetItem({
          libraryKey: librarySubAsset.libraryKey,
          name: librarySubAsset.name,
          src: librarySubAsset.src,
          storagePath: librarySubAsset.storagePath,
          ownerUid: librarySubAsset.ownerUid,
        })
      );
    }
  }

  for (const [fallbackLibraryKey, subAsset] of localByLibraryKey.entries()) {
    mergedSubAssets.push(
      createSubAssetItem({
        ...subAsset,
        libraryKey: subAsset.libraryKey || fallbackLibraryKey,
      })
    );
  }

  return mergedSubAssets;
}

function hydrateProjectFromMediaLibrary(project, mediaLibrary) {
  const normalizedProject = normalizeProject(project);
  const library = normalizeMediaLibrary(mediaLibrary);

  const projectMapsByLibraryKey = new Map(
    normalizedProject.maps.map((mapItem, index) => [
      mapItem.libraryKey || buildMapLibraryKey(mapItem.name || EMPTY_MAP_NAMES[index], index),
      mapItem,
    ])
  );
  const mergedMaps = [];

  for (const libraryMap of library.maps) {
    const existingMap = projectMapsByLibraryKey.get(libraryMap.libraryKey);
    if (existingMap) {
      mergedMaps.push(
        createMapItem({
          ...existingMap,
          libraryKey: libraryMap.libraryKey,
          name: libraryMap.name || existingMap.name,
          src: libraryMap.src || existingMap.src || "",
          storagePath: libraryMap.storagePath || existingMap.storagePath || null,
          ownerUid: libraryMap.ownerUid || existingMap.ownerUid || null,
        })
      );
      projectMapsByLibraryKey.delete(libraryMap.libraryKey);
    } else {
      mergedMaps.push(
        createMapItem({
          libraryKey: libraryMap.libraryKey,
          name: libraryMap.name,
          src: libraryMap.src,
          storagePath: libraryMap.storagePath,
          ownerUid: libraryMap.ownerUid,
        })
      );
    }
  }

  for (const [fallbackLibraryKey, mapItem] of projectMapsByLibraryKey.entries()) {
    mergedMaps.push(
      createMapItem({
        ...mapItem,
        libraryKey: mapItem.libraryKey || fallbackLibraryKey,
      })
    );
  }

  const projectAssetsByLibraryKey = new Map(
    normalizedProject.assets.map((assetItem, index) => [
      assetItem.libraryKey || buildStarterAssetLibraryKey(index),
      assetItem,
    ])
  );
  const mergedAssets = [];

  for (const libraryAsset of library.assets) {
    const existingAsset = projectAssetsByLibraryKey.get(libraryAsset.libraryKey);
    if (existingAsset) {
      mergedAssets.push(
        createAssetItem({
          ...existingAsset,
          libraryKey: libraryAsset.libraryKey,
          name: libraryAsset.name || existingAsset.name,
          src: libraryAsset.src || existingAsset.src || "",
          storagePath: libraryAsset.storagePath || existingAsset.storagePath || null,
          ownerUid: libraryAsset.ownerUid || existingAsset.ownerUid || null,
          subAssets: mergeSubAssetsWithLibrary(
            existingAsset.subAssets,
            libraryAsset.subAssets,
            libraryAsset.libraryKey
          ),
        })
      );
      projectAssetsByLibraryKey.delete(libraryAsset.libraryKey);
    } else {
      mergedAssets.push(
        createAssetItem({
          libraryKey: libraryAsset.libraryKey,
          name: libraryAsset.name,
          src: libraryAsset.src,
          storagePath: libraryAsset.storagePath,
          ownerUid: libraryAsset.ownerUid,
          subAssets: mergeSubAssetsWithLibrary([], libraryAsset.subAssets, libraryAsset.libraryKey),
        })
      );
    }
  }

  for (const [fallbackLibraryKey, assetItem] of projectAssetsByLibraryKey.entries()) {
    mergedAssets.push(
      createAssetItem({
        ...assetItem,
        libraryKey: assetItem.libraryKey || fallbackLibraryKey,
      })
    );
  }

  return {
    ...normalizedProject,
    maps: mergedMaps,
    assets: mergedAssets,
  };
}

function getEffectiveSequenceState(project, sequenceIndex) {
  for (let index = sequenceIndex; index >= 0; index -= 1) {
    const sequence = project.sequences[index];
    if (sequence?.edited) {
      return {
        sourceIndex: index,
        items: sequence.items.map((item) => ({ ...item })),
      };
    }
  }

  return {
    sourceIndex: -1,
    items: [],
  };
}

function ensureSequenceEditable(project, sequenceIndex) {
  const sequence = project.sequences[sequenceIndex];
  if (!sequence) {
    return null;
  }

  if (!sequence.edited) {
    const inherited = getEffectiveSequenceState(project, sequenceIndex);
    sequence.items = inherited.items.map((item) => ({ ...item }));
    sequence.edited = true;
  }

  return sequence;
}

function getTimelineSequenceEntries(project) {
  const editedEntries = project.sequences
    .map((sequence, index) =>
      sequence?.edited
        ? {
            sequenceIndex: index,
            items: sequence.items.map((item) => ({ ...item })),
          }
        : null
    )
    .filter(Boolean);

  if (editedEntries.length > 0) {
    return editedEntries;
  }

  const fallback = getEffectiveSequenceState(project, 0);
  return [
    {
      sequenceIndex: 0,
      items: fallback.items,
    },
  ];
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("Failed to read file."));
    reader.readAsDataURL(file);
  });
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("Failed to read blob."));
    reader.readAsDataURL(blob);
  });
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function hashText(text) {
  const encoder = new TextEncoder();
  return window.crypto.subtle.digest("SHA-256", encoder.encode(text)).then((buffer) =>
    Array.from(new Uint8Array(buffer))
      .map((value) => value.toString(16).padStart(2, "0"))
      .join("")
  );
}

function ensurePlannerDbStores(db) {
  if (!db) {
    return;
  }

  PLANNER_DB_REQUIRED_STORES.forEach((storeName) => {
    if (!db.objectStoreNames.contains(storeName)) {
      db.createObjectStore(storeName, { keyPath: "id" });
    }
  });
}

function getMissingPlannerDbStores(db) {
  if (!db) {
    return [...PLANNER_DB_REQUIRED_STORES];
  }

  return PLANNER_DB_REQUIRED_STORES.filter((storeName) => !db.objectStoreNames.contains(storeName));
}

function openPlannerDb(requestedVersion, allowRepair = true) {
  return new Promise((resolve, reject) => {
    if (!window.indexedDB) {
      reject(new Error("IndexedDB is not available in this browser."));
      return;
    }

    const request =
      Number.isFinite(requestedVersion) && requestedVersion > 0 ?
        window.indexedDB.open(DB_NAME, requestedVersion) :
        window.indexedDB.open(DB_NAME);

    request.onupgradeneeded = () => {
      const db = request.result;
      ensurePlannerDbStores(db);
    };

    request.onsuccess = () => {
      const db = request.result;
      const missingStores = getMissingPlannerDbStores(db);

      if (missingStores.length && allowRepair) {
        const nextVersion = Math.max(Number(db.version || 1) + 1, DB_VERSION);
        db.close();
        openPlannerDb(nextVersion, false)
          .then(resolve)
          .catch(reject);
        return;
      }

      if (missingStores.length) {
        db.close();
        reject(
          new Error(
            `Planner database is missing required stores: ${missingStores.join(", ")}.`,
          ),
        );
        return;
      }

      resolve(db);
    };
    request.onblocked = () => {
      reject(new Error("Planner database upgrade is blocked by another open tab or window."));
    };
    request.onerror = () => reject(request.error || new Error("Failed to open planner database."));
  });
}

function withStore(storeName, mode, callback) {
  return openPlannerDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const transaction = db.transaction(storeName, mode);
        const store = transaction.objectStore(storeName);
        let request;

        try {
          request = callback(store);
        } catch (error) {
          reject(error);
          db.close();
          return;
        }

        transaction.oncomplete = () => {
          db.close();
          resolve(request?.result);
        };
        transaction.onerror = () => {
          const txError = transaction.error || request?.error || new Error("IndexedDB transaction failed.");
          db.close();
          reject(txError);
        };
        transaction.onabort = () => {
          const txError = transaction.error || request?.error || new Error("IndexedDB transaction aborted.");
          db.close();
          reject(txError);
        };
      })
  );
}

function loadWorkspace() {
  return withStore("workspace", "readonly", (store) => store.get(WORKSPACE_KEY)).then((record) =>
    normalizeProject(record?.data || createInitialProject())
  );
}

function saveWorkspace(project) {
  const payload = {
    id: WORKSPACE_KEY,
    updatedAt: Date.now(),
    data: normalizeProject(project),
  };
  return withStore("workspace", "readwrite", (store) => store.put(payload));
}

function loadMediaLibrary() {
  return withStore("mediaLibrary", "readonly", (store) => store.get(MEDIA_LIBRARY_KEY)).then((record) =>
    normalizeMediaLibrary(record?.data || createEmptyMediaLibrary())
  );
}

function saveMediaLibrary(library) {
  const payload = {
    id: MEDIA_LIBRARY_KEY,
    updatedAt: Date.now(),
    data: normalizeMediaLibrary(library),
  };
  return withStore("mediaLibrary", "readwrite", (store) => store.put(payload));
}

function listSavedProjects() {
  return withStore("savedProjects", "readonly", (store) => store.getAll()).then((items = []) =>
    items.sort((left, right) => (right.updatedAt || 0) - (left.updatedAt || 0))
  );
}

function saveProjectSnapshot(projectId, projectName, project) {
  const payload = {
    id: projectId || uid("saved-project"),
    projectName: projectName || project.projectName || "Untitled Snapshot",
    updatedAt: Date.now(),
    data: normalizeProject(project),
  };
  return withStore("savedProjects", "readwrite", (store) => store.put(payload)).then(() => payload);
}

function loadProjectSnapshot(projectId) {
  return withStore("savedProjects", "readonly", (store) => store.get(projectId)).then((record) =>
    record ? normalizeProject(record.data) : null
  );
}

function deleteProjectSnapshot(projectId) {
  return withStore("savedProjects", "readwrite", (store) => store.delete(projectId));
}

function loadImageSource(source, cache) {
  if (!source) {
    return Promise.resolve(null);
  }
  if (cache.has(source)) {
    return cache.get(source);
  }
  const promise = new Promise((resolve, reject) => {
    const image = new Image();
    if (/^https?:\/\//i.test(source)) {
      image.crossOrigin = "anonymous";
    }
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Failed to load image: ${source}`));
    image.src = source;
  });
  cache.set(source, promise);
  return promise;
}

function getStaticRenderState(project, sequenceIndex) {
  const effectiveSequence = getEffectiveSequenceState(project, sequenceIndex);
  return {
    fromMapId: project.backgroundMapId,
    toMapId: project.backgroundMapId,
    mapMix: 0,
    items: [...effectiveSequence.items].sort((left, right) => left.zIndex - right.zIndex),
    activeSequenceIndex: sequenceIndex,
    nextSequenceIndex: sequenceIndex,
    sourceSequenceIndex: effectiveSequence.sourceIndex,
    inTransition: false,
  };
}

function interpolateSequenceItems(fromItems, toItems, progress) {
  const fromById = new Map(fromItems.map((item) => [item.instanceId, item]));
  const toById = new Map(toItems.map((item) => [item.instanceId, item]));
  const ids = new Set([...fromById.keys(), ...toById.keys()]);

  return Array.from(ids)
    .map((instanceId) => {
      const fromItem = fromById.get(instanceId) || null;
      const toItem = toById.get(instanceId) || null;

      if (fromItem && toItem) {
        return {
          ...toItem,
          x: lerp(fromItem.x, toItem.x, progress),
          y: lerp(fromItem.y, toItem.y, progress),
          width: lerp(fromItem.width, toItem.width, progress),
          height: lerp(fromItem.height, toItem.height, progress),
          rotation: lerp(fromItem.rotation, toItem.rotation, progress),
          opacity: lerp(fromItem.opacity ?? 1, toItem.opacity ?? 1, progress),
          zIndex: progress < 0.5 ? fromItem.zIndex : toItem.zIndex,
          flipX: progress < 0.5 ? fromItem.flipX : toItem.flipX,
          flipY: progress < 0.5 ? fromItem.flipY : toItem.flipY,
        };
      }

      if (fromItem) {
        return {
          ...fromItem,
          opacity: (fromItem.opacity ?? 1) * (1 - progress),
        };
      }

      return {
        ...toItem,
        opacity: (toItem.opacity ?? 1) * progress,
      };
    })
    .sort((left, right) => left.zIndex - right.zIndex);
}

function getTimelineDuration(project) {
  const holdMs = project.exportSettings.frameHoldMs;
  const transitionMs = project.exportSettings.transitionMs;
  const sequenceCount = getTimelineSequenceEntries(project).length;
  if (sequenceCount <= 1) {
    return holdMs;
  }
  return sequenceCount * holdMs + (sequenceCount - 1) * transitionMs;
}

function getPlaybackRenderState(project, elapsedMs) {
  const sequences = getTimelineSequenceEntries(project);
  const holdMs = project.exportSettings.frameHoldMs;
  const transitionMs = project.exportSettings.transitionMs;

  if (sequences.length <= 1) {
    return getStaticRenderState(project, sequences[0]?.sequenceIndex || 0);
  }

  let cursor = 0;
  for (let index = 0; index < sequences.length; index += 1) {
    const current = sequences[index];
    const next = sequences[index + 1] || null;

    if (elapsedMs < cursor + holdMs || !next) {
      return {
        fromMapId: project.backgroundMapId,
        toMapId: project.backgroundMapId,
        mapMix: 0,
        items: [...current.items].sort((left, right) => left.zIndex - right.zIndex),
        activeSequenceIndex: current.sequenceIndex,
        nextSequenceIndex: current.sequenceIndex,
        inTransition: false,
      };
    }
    cursor += holdMs;

    if (transitionMs > 0 && elapsedMs < cursor + transitionMs) {
      const progress = clamp((elapsedMs - cursor) / transitionMs, 0, 1);
      return {
        fromMapId: project.backgroundMapId,
        toMapId: project.backgroundMapId,
        mapMix: progress,
        items: interpolateSequenceItems(current.items, next.items, progress),
        activeSequenceIndex: current.sequenceIndex,
        nextSequenceIndex: next.sequenceIndex,
        inTransition: true,
      };
    }
    cursor += transitionMs;
  }

  return getStaticRenderState(project, sequences[sequences.length - 1]?.sequenceIndex || 0);
}

async function drawRenderStateToCanvas(ctx, project, renderState, imageCache) {
  const { width, height } = project.canvas;
  const assetLookup = new Map(project.assets.map((asset) => [asset.id, asset]));
  const mapLookup = new Map(project.maps.map((mapItem) => [mapItem.id, mapItem]));

  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = "#f4eee0";
  ctx.fillRect(0, 0, width, height);

  const drawMapById = async (mapId, alpha) => {
    if (!mapId || alpha <= 0) return;
    const mapItem = mapLookup.get(mapId);
    if (!mapItem?.src) return;
    const image = await loadImageSource(mapItem.src, imageCache);
    if (!image) return;

    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.drawImage(image, 0, 0, width, height);
    ctx.restore();
  };

  if (renderState.toMapId && renderState.fromMapId !== renderState.toMapId && renderState.mapMix > 0) {
    await drawMapById(renderState.fromMapId, 1 - renderState.mapMix);
    await drawMapById(renderState.toMapId, renderState.mapMix);
  } else {
    await drawMapById(renderState.fromMapId || renderState.toMapId, 1);
  }

  for (const item of renderState.items) {
    const media = getSequenceItemMedia(assetLookup, item);
    if (!media?.src || item.opacity <= 0) continue;
    const image = await loadImageSource(media.src, imageCache);
    if (!image) continue;

    ctx.save();
    ctx.globalAlpha = item.opacity ?? 1;
    ctx.translate(item.x + item.width / 2, item.y + item.height / 2);
    ctx.rotate((item.rotation * Math.PI) / 180);
    ctx.scale(item.flipX ? -1 : 1, item.flipY ? -1 : 1);
    ctx.drawImage(image, -item.width / 2, -item.height / 2, item.width, item.height);
    ctx.restore();
  }
}

function buildResizeResult(startItem, direction, deltaX, deltaY) {
  const minSize = 20;
  let x = startItem.x;
  let y = startItem.y;
  let width = startItem.width;
  let height = startItem.height;

  if (direction.length === 2) {
    const scaleX =
      direction.includes("w")
        ? (startItem.width - deltaX) / startItem.width
        : (startItem.width + deltaX) / startItem.width;
    const scaleY =
      direction.includes("n")
        ? (startItem.height - deltaY) / startItem.height
        : (startItem.height + deltaY) / startItem.height;
    const scale = Math.max(minSize / startItem.width, minSize / startItem.height, scaleX, scaleY);

    width = Math.max(minSize, startItem.width * scale);
    height = Math.max(minSize, startItem.height * scale);

    if (direction.includes("w")) {
      x = startItem.x + (startItem.width - width);
    }
    if (direction.includes("n")) {
      y = startItem.y + (startItem.height - height);
    }
  } else {
    if (direction === "e") {
      width = Math.max(minSize, startItem.width + deltaX);
    }
    if (direction === "s") {
      height = Math.max(minSize, startItem.height + deltaY);
    }
    if (direction === "w") {
      width = Math.max(minSize, startItem.width - deltaX);
      x = startItem.x + (startItem.width - width);
    }
    if (direction === "n") {
      height = Math.max(minSize, startItem.height - deltaY);
      y = startItem.y + (startItem.height - height);
    }
  }

  return { x, y, width, height };
}

function isEditableTag(element) {
  const tagName = element?.tagName || "";
  return tagName === "INPUT" || tagName === "TEXTAREA" || tagName === "SELECT";
}

function MapSettingsModal({
  map,
  onClose,
  onRename,
  onReplaceSource,
  onCropSource,
  onMoveEarlier,
  onMoveLater,
  canMoveEarlier,
  canMoveLater,
  onDelete,
}) {
  const [isCropping, setIsCropping] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isSavingCrop, setIsSavingCrop] = useState(false);

  useEffect(() => {
    setIsCropping(false);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setIsSavingCrop(false);
  }, [map?.id]);

  const handleCropComplete = useCallback((_, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleSaveCrop = useCallback(async () => {
    if (!map?.src || !croppedAreaPixels) return;
    setIsSavingCrop(true);
    try {
      await onCropSource(croppedAreaPixels);
      setIsCropping(false);
    } finally {
      setIsSavingCrop(false);
    }
  }, [croppedAreaPixels, map?.src, onCropSource]);

  if (!map) return null;

  return (
    <div className="StratsPlannerModal__backdrop" role="presentation" onClick={onClose}>
      <div
        className="StratsPlannerModal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="strats-planner-map-settings-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="StratsPlannerModal__header">
          <div>
            <div className="StratsPlannerPanel__eyebrow">Map Settings</div>
            <h3 id="strats-planner-map-settings-title">{map.name}</h3>
          </div>
          <button type="button" className="StratsPlannerButton StratsPlannerButton--ghost app-cancel-control" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="StratsPlannerModal__body">
          <label className="StratsPlannerField">
            <span>Map name</span>
            <input
              type="text"
              value={map.name}
              onChange={(event) => onRename(event.target.value)}
            />
          </label>

          <div className="StratsPlannerField">
            <span>Background image</span>
            <div className="StratsPlannerUploadRow StratsPlannerUploadRow--topAligned">
              <div className="StratsPlannerThumb StratsPlannerThumb--large">
                {map.src ? <img src={map.src} alt={map.name} /> : <span>No image yet</span>}
              </div>
              <div className="StratsPlannerStackActions">
                <label className="StratsPlannerButton StratsPlannerButton--ghost">
                  {map.src ? "Replace image" : "Upload image"}
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(event) => onReplaceSource(event.target.files?.[0] || null)}
                  />
                </label>

                {map.src ? (
                  <button
                    type="button"
                    className={((((isCropping) && (true))) ? 'app-cancel-control' : '') + ' ' + ("StratsPlannerButton StratsPlannerButton--ghost" || '')}
                    onClick={() => setIsCropping((current) => !current)}
                  >
                    {isCropping ? "Cancel cut" : "Cut background image"}
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          <div className="StratsPlannerField">
            <span>Library order</span>
            <div className="StratsPlannerSequenceActions StratsPlannerSequenceActions--compact">
              <button
                type="button"
                className="StratsPlannerButton StratsPlannerButton--ghost"
                onClick={onMoveEarlier}
                disabled={!canMoveEarlier}
              >
                Move earlier
              </button>
              <button
                type="button"
                className="StratsPlannerButton StratsPlannerButton--ghost"
                onClick={onMoveLater}
                disabled={!canMoveLater}
              >
                Move later
              </button>
            </div>
          </div>

          {isCropping && map.src ? (
            <div className="StratsPlannerField">
              <span>Crop background image</span>
              <div className="StratsPlannerCropperShell">
                <Cropper
                  image={map.src}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  showGrid
                  objectFit="contain"
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={handleCropComplete}
                  style={{
                    containerStyle: { width: "100%", height: "100%" },
                    cropAreaStyle: { border: "2px solid #fff" },
                  }}
                />
              </div>

              <label className="StratsPlannerField">
                <span>Zoom</span>
                <input
                  type="range"
                  min="1"
                  max="3"
                  step="0.05"
                  value={zoom}
                  onChange={(event) => setZoom(Number(event.target.value))}
                />
              </label>

              <div className="StratsPlannerSequenceActions StratsPlannerSequenceActions--compact">
                <button
                  type="button"
                  className="StratsPlannerButton"
                  disabled={!croppedAreaPixels || isSavingCrop}
                  onClick={handleSaveCrop}
                >
                  {isSavingCrop ? "Saving..." : "Save cut"}
                </button>
                <button
                  type="button"
                  className="StratsPlannerButton StratsPlannerButton--ghost"
                  onClick={() => setIsCropping(false)}
                >
                  Keep original
                </button>
              </div>
            </div>
          ) : null}

          <div className="StratsPlannerField">
            <span>Danger zone</span>
            <div className="StratsPlannerSequenceActions StratsPlannerSequenceActions--compact">
              <button type="button" className="StratsPlannerButton StratsPlannerButton--danger" onClick={onDelete}>
                Delete map
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AssetSettingsModal({
  asset,
  onClose,
  onRename,
  onReplaceSource,
  onDelete,
  onMoveEarlier,
  onMoveLater,
  canMoveEarlier,
  canMoveLater,
  onAddSubAsset,
  onRenameSubAsset,
  onRemoveSubAsset,
  onReplaceSubAssetSource,
}) {
  if (!asset) return null;

  return (
    <div className="StratsPlannerModal__backdrop" role="presentation" onClick={onClose}>
      <div
        className="StratsPlannerModal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="strats-planner-asset-settings-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="StratsPlannerModal__header">
          <div>
            <div className="StratsPlannerPanel__eyebrow">Asset Settings</div>
            <h3 id="strats-planner-asset-settings-title">{asset.name}</h3>
          </div>
          <button type="button" className="StratsPlannerButton StratsPlannerButton--ghost app-cancel-control" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="StratsPlannerModal__body">
          <label className="StratsPlannerField">
            <span>Name</span>
            <input
              type="text"
              value={asset.name}
              onChange={(event) => onRename(event.target.value)}
            />
          </label>

          <div className="StratsPlannerField">
            <span>Main image</span>
            <div className="StratsPlannerUploadRow">
              <div className="StratsPlannerThumb StratsPlannerThumb--large">
                {asset.src ? <img src={asset.src} alt={asset.name} /> : <span>No image yet</span>}
              </div>
              <label className="StratsPlannerButton StratsPlannerButton--ghost">
                Replace image
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(event) => onReplaceSource(event.target.files?.[0] || null)}
                />
              </label>
            </div>
          </div>

          <div className="StratsPlannerField">
            <span>Library order</span>
            <div className="StratsPlannerSequenceActions StratsPlannerSequenceActions--compact">
              <button
                type="button"
                className="StratsPlannerButton StratsPlannerButton--ghost"
                onClick={onMoveEarlier}
                disabled={!canMoveEarlier}
              >
                Move earlier
              </button>
              <button
                type="button"
                className="StratsPlannerButton StratsPlannerButton--ghost"
                onClick={onMoveLater}
                disabled={!canMoveLater}
              >
                Move later
              </button>
            </div>
          </div>

          <div className="StratsPlannerField">
            <div className="StratsPlannerField__row">
              <span>Agent Abilities</span>
              <label className="StratsPlannerButton StratsPlannerButton--ghost">
                + Add Agent Ability
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(event) => onAddSubAsset(event.target.files?.[0] || null)}
                />
              </label>
            </div>

            <div className="StratsPlannerSubAssetList">
              {asset.subAssets.length === 0 && (
                <div className="StratsPlannerEmpty">
                  Agent Abilities appear beneath this image in the left rail when you hover it.
                </div>
              )}

              {asset.subAssets.map((subAsset) => (
                <div key={subAsset.id} className="StratsPlannerSubAssetCard">
                  <div className="StratsPlannerThumb">
                    {subAsset.src ? <img src={subAsset.src} alt={subAsset.name} /> : <span>Empty</span>}
                  </div>

                  <input
                    type="text"
                    value={subAsset.name}
                    onChange={(event) => onRenameSubAsset(subAsset.id, event.target.value)}
                  />

                  <div className="StratsPlannerSubAssetCard__actions">
                    <label className="StratsPlannerButton StratsPlannerButton--ghost">
                      Replace
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={(event) =>
                          onReplaceSubAssetSource(subAsset.id, event.target.files?.[0] || null)
                        }
                      />
                    </label>
                    <button
                      type="button"
                      className="StratsPlannerButton StratsPlannerButton--danger"
                      onClick={() => onRemoveSubAsset(subAsset.id)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="StratsPlannerField">
            <span>Danger zone</span>
            <div className="StratsPlannerSequenceActions StratsPlannerSequenceActions--compact">
              <button type="button" className="StratsPlannerButton StratsPlannerButton--danger" onClick={onDelete}>
                Delete agent entry
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PlannerIconButton({ label, onClick, className = "", disabled = false, children }) {
  return (
    <button
      type="button"
      className={`StratsPlannerIconButton ${className}`.trim()}
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

function IconPlay() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M8 5L19 12L8 19Z" fill="currentColor" />
    </svg>
  );
}

function IconReset() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M6 9V4M6 4H11M6 4L9 7C10.6 5.7 12.6 5 14.8 5C19.9 5 24 9.1 24 14.2C24 19.3 19.9 23.4 14.8 23.4C10.7 23.4 7.1 20.8 5.9 17"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconStepClear() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 6H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M9 6V4H15V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M8 10V18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M12 10V18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M16 10V18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M6.5 6L7.3 20H16.7L17.5 6" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

function IconClearAll() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 4L20 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M20 4L4 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M7 7H17V17H7Z" fill="none" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function IconSettings() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M12 8.5A3.5 3.5 0 1 0 12 15.5A3.5 3.5 0 1 0 12 8.5Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        d="M19.4 15A1.65 1.65 0 0 0 19.73 16.82L19.79 16.88A2 2 0 1 1 16.96 19.71L16.9 19.65A1.65 1.65 0 0 0 15.08 19.32A1.65 1.65 0 0 0 14 20.85V21A2 2 0 1 1 10 21V20.91A1.65 1.65 0 0 0 8.92 19.38A1.65 1.65 0 0 0 7.1 19.71L7.04 19.77A2 2 0 1 1 4.21 16.94L4.27 16.88A1.65 1.65 0 0 0 4.6 15.06A1.65 1.65 0 0 0 3.07 14H3A2 2 0 1 1 3 10H3.09A1.65 1.65 0 0 0 4.62 8.92A1.65 1.65 0 0 0 4.29 7.1L4.23 7.04A2 2 0 1 1 7.06 4.21L7.12 4.27A1.65 1.65 0 0 0 8.94 4.6H9A1.65 1.65 0 0 0 10 3.09V3A2 2 0 1 1 14 3V3.09A1.65 1.65 0 0 0 15.08 4.62A1.65 1.65 0 0 0 16.9 4.29L16.96 4.23A2 2 0 1 1 19.79 7.06L19.73 7.12A1.65 1.65 0 0 0 19.4 8.94V9A1.65 1.65 0 0 0 20.93 10H21A2 2 0 1 1 21 14H20.91A1.65 1.65 0 0 0 19.38 15.08Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconHelp() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
      <path
        d="M9.85 9.1C10.14 7.87 11.16 7 12.45 7C13.96 7 15.08 8.06 15.08 9.43C15.08 10.55 14.52 11.18 13.22 12.02C12.5 12.49 12.14 13 12.14 13.8"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <circle cx="12" cy="17.2" r="1.1" fill="currentColor" />
    </svg>
  );
}

function IconChevronUp() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M6 15L12 9L18 15"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconChevronDown() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M6 9L12 15L18 9"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconLoad() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 4V14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M8.5 10.5L12 14L15.5 10.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5 18H19" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconTrash() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 6H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M9 6V4H15V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M8 10V18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M12 10V18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M16 10V18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M6.5 6L7.3 20H16.7L17.5 6" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

function IconFilter() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M4 6H20L14 13V18L10 20V13L4 6Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function AdminStratsPlannerPage() {
  const { userData } = useAuth();
  const guideMediaManifestVersion = useGuideMediaManifestVersion();
  const [gateChecked, setGateChecked] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const [project, setProject] = useState(() => createInitialProject());
  const [workspaceLoaded, setWorkspaceLoaded] = useState(false);
  const [activeSequenceIndex, setActiveSequenceIndex] = useState(0);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [isMapPanelOpen, setIsMapPanelOpen] = useState(false);
  const [selectedMapId, setSelectedMapId] = useState("");
  const [mapSettingsId, setMapSettingsId] = useState(null);
  const [assetSettingsId, setAssetSettingsId] = useState(null);
  const [savedProjects, setSavedProjects] = useState([]);
  const [selectedSavedProjectId, setSelectedSavedProjectId] = useState("");
  const [isExportMenuExpanded, setIsExportMenuExpanded] = useState(true);
  const [highlightUsedAssetsOnly, setHighlightUsedAssetsOnly] = useState(false);
  const [toast, setToast] = useState("");
  const [exporting, setExporting] = useState(false);
  const [publishingGuide, setPublishingGuide] = useState(false);
  const [isPlayingPreview, setIsPlayingPreview] = useState(false);
  const [previewRenderState, setPreviewRenderState] = useState(() => null);
  const [selectedGuidePublishRoutePath, setSelectedGuidePublishRoutePath] = useState("");
  const [selectedGuidePublishSectionId, setSelectedGuidePublishSectionId] = useState("");
  const [selectedGuidePublishSlotId, setSelectedGuidePublishSlotId] = useState("");

  const stageRef = useRef(null);
  const autosaveTimerRef = useRef(null);
  const interactionRef = useRef(null);
  const previewFrameRef = useRef(null);
  const previewStartedAtRef = useRef(null);
  const previewLastSelectionRef = useRef(0);
  const imageCacheRef = useRef(new Map());
  const toastTimerRef = useRef(null);
  const pendingMediaLibrarySyncRef = useRef(false);

  const activeSequence = useMemo(
    () => getEffectiveSequenceState(project, activeSequenceIndex),
    [activeSequenceIndex, project]
  );
  const selectedItem = activeSequence.items.find((item) => item.instanceId === selectedItemId) || null;
  const assetLookup = useMemo(
    () => new Map(project.assets.map((assetItem) => [assetItem.id, assetItem])),
    [project.assets]
  );
  const mapLookup = useMemo(
    () => new Map(project.maps.map((mapItem) => [mapItem.id, mapItem])),
    [project.maps]
  );
  const currentRenderState = isPlayingPreview
    ? previewRenderState || getStaticRenderState(project, activeSequenceIndex)
    : getStaticRenderState(project, activeSequenceIndex);
  const selectedAsset = project.assets.find((assetItem) => assetItem.id === assetSettingsId) || null;
  const selectedMapForSettings = project.maps.find((mapItem) => mapItem.id === mapSettingsId) || null;
  const selectedSavedProject = savedProjects.find((savedProject) => savedProject.id === selectedSavedProjectId) || null;
  const guidePublishRoutes = useMemo(() => getGuidePublishRoutes(), []);
  const guidePublishSections = useMemo(
    () => getGuidePublishSections(selectedGuidePublishRoutePath),
    [selectedGuidePublishRoutePath]
  );
  const guidePublishSlots = useMemo(
    () => getGuidePublishSlotsForSection(selectedGuidePublishRoutePath, selectedGuidePublishSectionId),
    [selectedGuidePublishRoutePath, selectedGuidePublishSectionId]
  );
  const selectedGuidePublishSlot = useMemo(
    () => getGuidePublishSlotById(selectedGuidePublishSlotId),
    [selectedGuidePublishSlotId]
  );
  const selectedGuidePublishAsset =
    guideMediaManifestVersion >= 0 && selectedGuidePublishSlot?.plannedAssetPath ?
      getGuideMediaAsset(selectedGuidePublishSlot.plannedAssetPath) :
      null;
  const usedAssetIds = useMemo(() => {
    const ids = new Set();
    project.sequences.forEach((sequence) => {
      sequence.items.forEach((item) => {
        if (item.assetId) {
          ids.add(item.assetId);
        }
      });
    });
    return ids;
  }, [project.sequences]);
  const visibleAssetItems = useMemo(() => {
    if (!highlightUsedAssetsOnly) {
      return project.assets;
    }

    return project.assets.filter((assetItem) => usedAssetIds.has(assetItem.id));
  }, [highlightUsedAssetsOnly, project.assets, usedAssetIds]);
  const selectedItemMedia = useMemo(() => {
    if (!selectedItem) {
      return null;
    }

    return getSequenceItemMedia(assetLookup, selectedItem);
  }, [assetLookup, selectedItem]);

  useEffect(() => {
    if (!guidePublishRoutes.length) {
      if (selectedGuidePublishRoutePath) {
        setSelectedGuidePublishRoutePath("");
      }
      return;
    }

    const hasSelectedRoute = guidePublishRoutes.some(
      (route) => route.routePath === selectedGuidePublishRoutePath
    );
    if (!hasSelectedRoute) {
      setSelectedGuidePublishRoutePath(guidePublishRoutes[0].routePath);
    }
  }, [guidePublishRoutes, selectedGuidePublishRoutePath]);

  useEffect(() => {
    if (!guidePublishSections.length) {
      if (selectedGuidePublishSectionId) {
        setSelectedGuidePublishSectionId("");
      }
      return;
    }

    const hasSelectedSection = guidePublishSections.some(
      (section) => section.sectionId === selectedGuidePublishSectionId
    );
    if (!hasSelectedSection) {
      setSelectedGuidePublishSectionId(guidePublishSections[0].sectionId);
    }
  }, [guidePublishSections, selectedGuidePublishSectionId]);

  useEffect(() => {
    if (!guidePublishSlots.length) {
      if (selectedGuidePublishSlotId) {
        setSelectedGuidePublishSlotId("");
      }
      return;
    }

    const hasSelectedSlot = guidePublishSlots.some(
      (slot) => slot.id === selectedGuidePublishSlotId
    );
    if (!hasSelectedSlot) {
      setSelectedGuidePublishSlotId(guidePublishSlots[0].id);
    }
  }, [guidePublishSlots, selectedGuidePublishSlotId]);

  const showToast = useCallback((message) => {
    setToast(message);
    window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => {
      setToast("");
    }, 3500);
  }, []);

  const commitProject = useCallback((mutator) => {
    setProject((current) => {
      const draft = cloneValue(current);
      mutator(draft);
      draft.updatedAt = Date.now();
      return normalizeProject(draft);
    });
  }, []);

  const refreshSavedProjects = useCallback(() => {
    return listSavedProjects()
      .then((items) => {
        setSavedProjects(items);
      })
      .catch((error) => {
        console.error("Strats planner: failed to list saved projects", error);
      });
  }, []);

  const persistSharedPlannerMediaLibrary = useCallback(
    async (librarySnapshot) => {
      if (!userData?.uid || !FUNCTION_NAMES.saveStratsPlannerMediaLibraryManifest) {
        return;
      }

      const callable = httpsCallable(
        firebaseFunctions,
        FUNCTION_NAMES.saveStratsPlannerMediaLibraryManifest
      );

      await callable({
        scope: STRATS_PLANNER_SHARED_LIBRARY_SCOPE,
        library: librarySnapshot,
      });
    },
    [userData?.uid]
  );

  useEffect(() => {
    const storedUnlock = sessionStorage.getItem(UNLOCK_SESSION_KEY) === "1";
    setIsUnlocked(storedUnlock);
    setGateChecked(true);
  }, []);

  useEffect(() => {
    let isActive = true;

    Promise.all([loadWorkspace(), loadMediaLibrary()])
      .then(([loadedProject, localMediaLibrary]) => {
        if (!isActive) return;
        const hydratedProject = hydrateProjectFromMediaLibrary(loadedProject, localMediaLibrary);
        setProject(hydratedProject);
        setSelectedMapId(hydratedProject.backgroundMapId || hydratedProject.maps[0]?.id || "");
        setWorkspaceLoaded(true);
      })
      .catch((error) => {
        console.error("Strats planner: failed to load workspace", error);
        if (!isActive) return;
        const fallback = normalizeProject(createInitialProject());
        setProject(fallback);
        setSelectedMapId(fallback.backgroundMapId || fallback.maps[0]?.id || "");
        setWorkspaceLoaded(true);
      });

    refreshSavedProjects();

    return () => {
      isActive = false;
    };
  }, [refreshSavedProjects]);

  useEffect(() => {
    if (!workspaceLoaded || !userData?.uid) {
      return undefined;
    }

    let isActive = true;

    Promise.all([
      loadMediaLibrary(),
      loadSharedStratsPlannerMediaLibrary({
        storage,
        scope: STRATS_PLANNER_SHARED_LIBRARY_SCOPE,
      }),
    ])
      .then(([localMediaLibrary, sharedMediaLibrary]) => {
        if (!isActive || !sharedMediaLibrary) {
          return;
        }

        const mergedLibrary = mergeMediaLibraries(sharedMediaLibrary, localMediaLibrary);
        saveMediaLibrary(mergedLibrary).catch((error) => {
          console.error("Strats planner: failed to refresh local planner media cache", error);
        });
        setProject((current) => hydrateProjectFromMediaLibrary(current, mergedLibrary));
      })
      .catch((error) => {
        console.error("Strats planner: failed to load shared planner media library", error);
      });

    return () => {
      isActive = false;
    };
  }, [userData?.uid, workspaceLoaded]);

  useEffect(() => {
    if (!workspaceLoaded) return undefined;
    window.clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = window.setTimeout(() => {
      saveWorkspace(project).catch((error) => {
        console.error("Strats planner: failed to autosave workspace", error);
      });
    }, 600);

    return () => {
      window.clearTimeout(autosaveTimerRef.current);
    };
  }, [project, workspaceLoaded]);

  useEffect(() => {
    if (!workspaceLoaded || !pendingMediaLibrarySyncRef.current) {
      return undefined;
    }

    pendingMediaLibrarySyncRef.current = false;
    const librarySnapshot = buildMediaLibrarySnapshot(project);
    saveMediaLibrary(librarySnapshot).catch((error) => {
      console.error("Strats planner: failed to persist media library", error);
    });

    if (userData?.uid) {
      persistSharedPlannerMediaLibrary(librarySnapshot).catch((error) => {
        console.error("Strats planner: failed to persist shared planner media library", error);
      });
    }

    return undefined;
  }, [persistSharedPlannerMediaLibrary, project, userData?.uid, workspaceLoaded]);

  useEffect(() => {
    if (!selectedItemId) return;
    const exists = activeSequence.items.some((item) => item.instanceId === selectedItemId);
    if (!exists) {
      setSelectedItemId(null);
    }
  }, [activeSequence, selectedItemId]);

  useEffect(() => {
    return () => {
      window.clearTimeout(toastTimerRef.current);
      window.clearTimeout(autosaveTimerRef.current);
      if (previewFrameRef.current) {
        window.cancelAnimationFrame(previewFrameRef.current);
      }
    };
  }, []);

  const handleUnlock = useCallback(async () => {
    try {
      const digest = await hashText(passwordInput);
      if (digest === PASSWORD_HASH) {
        sessionStorage.setItem(UNLOCK_SESSION_KEY, "1");
        setIsUnlocked(true);
        setPasswordError("");
        setPasswordInput("");
        return;
      }

      setPasswordError("Password did not match.");
    } catch (error) {
      console.error("Strats planner: password check failed", error);
      setPasswordError("Unable to verify the password in this browser.");
    }
  }, [passwordInput]);

  const queueMediaLibrarySync = useCallback(() => {
    pendingMediaLibrarySyncRef.current = true;
  }, []);

  const uploadPlannerImageSource = useCallback(
    async ({
      file = null,
      blob = null,
      fileName,
      contentType,
      mediaType,
      libraryKey,
      variantKey = null,
      localFallbackLabel,
    }) => {
      if (!file && !blob) {
        return null;
      }

      if (!userData?.uid) {
        if (file) {
          const localSrc = await fileToDataUrl(file);
          showToast(`${localFallbackLabel} stored locally because no signed-in Firebase user is available.`);
          return {
            src: localSrc,
            storagePath: null,
            ownerUid: null,
          };
        }

        if (blob) {
          const localBlobUrl = await blobToDataUrl(blob);
          showToast(`${localFallbackLabel} stored locally because no signed-in Firebase user is available.`);
          return {
            src: localBlobUrl,
            storagePath: null,
            ownerUid: null,
          };
        }
      }

      const uploadedMedia = await uploadStratsPlannerMedia({
        storage,
        ownerUid: userData.uid,
        mediaType,
        libraryKey,
        variantKey,
        file,
        blob,
        fileName,
        contentType,
      });

      return {
        src: uploadedMedia.src,
        storagePath: uploadedMedia.storagePath,
        ownerUid: uploadedMedia.ownerUid,
      };
    },
    [showToast, userData?.uid]
  );

  const cleanupPriorPlannerMedia = useCallback(
    async (storagePath, ownerUid) => {
      await deleteStratsPlannerMedia({
        storage,
        storagePath,
        ownerUid,
        currentUid: userData?.uid || null,
      });
    },
    [userData?.uid]
  );


  const addAssetToCurrentSequence = useCallback(
    (assetId, subAssetId = null) => {
      const asset = assetLookup.get(assetId);
      if (!asset) return;
      const media = subAssetId ? findSubAssetById(asset, subAssetId) : asset;
      if (!media?.src) {
        setAssetSettingsId(assetId);
        showToast(
          subAssetId
            ? "Upload an Agent Ability image for this slot before placing it on the canvas."
            : "Upload a main image for this slot before placing it on the canvas."
        );
        return;
      }

      let nextSelectedId = "";
      commitProject((draft) => {
        const sequence = ensureSequenceEditable(draft, activeSequenceIndex);
        if (!sequence) return;
        const instanceId = uid("item");
        const topZIndex =
          sequence.items.reduce((highest, item) => Math.max(highest, item.zIndex), 0) + 1;
        const width = 140;
        const height = 140;
        const offset = sequence.items.length * 18;

        sequence.items.push({
          instanceId,
          assetId,
          subAssetId,
          x: clamp(120 + offset, 0, Math.max(0, draft.canvas.width - width)),
          y: clamp(120 + offset, 0, Math.max(0, draft.canvas.height - height)),
          width,
          height,
          rotation: 0,
          flipX: false,
          flipY: false,
          zIndex: topZIndex,
          opacity: 1,
        });
        nextSelectedId = instanceId;
      });

      setSelectedItemId(nextSelectedId);
    },
    [activeSequenceIndex, assetLookup, commitProject, showToast]
  );

  const updateSelectedItem = useCallback(
    (mutator) => {
      if (!selectedItemId) return;
      commitProject((draft) => {
        const sequence = ensureSequenceEditable(draft, activeSequenceIndex);
        if (!sequence) return;
        const item = sequence.items.find((entry) => entry.instanceId === selectedItemId);
        if (!item) return;
        mutator(item, draft);
      });
    },
    [activeSequenceIndex, commitProject, selectedItemId]
  );

  const handleMapSelect = useCallback(
    (mapId) => {
      setSelectedMapId(mapId);
      commitProject((draft) => {
        draft.backgroundMapId = mapId;
      });
    },
    [commitProject]
  );

  const handleAddMap = useCallback(() => {
    let nextMapId = "";
    const nextLibraryKey = `custom-map-${uid("library")}`;
    queueMediaLibrarySync();
    commitProject((draft) => {
      const mapName = `Custom Map ${draft.maps.length + 1}`;
      nextMapId = uid("map");
      draft.maps.push(
        createMapItem({
          id: nextMapId,
          libraryKey: nextLibraryKey,
          name: mapName,
          src: "",
          storagePath: null,
          ownerUid: null,
        })
      );
      draft.backgroundMapId = nextMapId;
    });
    setSelectedMapId(nextMapId);
    setMapSettingsId(nextMapId);
    setIsMapPanelOpen(true);
  }, [commitProject, queueMediaLibrarySync]);

  const handleMapRename = useCallback(
    (mapId, value) => {
      if (!mapId) return;
      queueMediaLibrarySync();
      commitProject((draft) => {
        const mapItem = draft.maps.find((entry) => entry.id === mapId);
        if (mapItem) {
          mapItem.name = value || "Untitled Map";
        }
      });
    },
    [commitProject, queueMediaLibrarySync]
  );

  const handleMapSourceReplace = useCallback(
    async (mapId, file) => {
      if (!file || !mapId) return;
      const currentMap = project.maps.find((entry) => entry.id === mapId);
      if (!currentMap) return;
      const nextSource = await uploadPlannerImageSource({
        file,
        mediaType: "maps",
        libraryKey: currentMap.libraryKey || mapId,
        fileName: file.name || `${currentMap.name || "map"}.png`,
        contentType: file.type || "image/png",
        localFallbackLabel: "Map image",
      });
      if (!nextSource) return;

      queueMediaLibrarySync();
      commitProject((draft) => {
        const mapItem = draft.maps.find((entry) => entry.id === mapId);
        if (mapItem) {
          mapItem.src = nextSource.src;
          mapItem.storagePath = nextSource.storagePath;
          mapItem.ownerUid = nextSource.ownerUid;
        }
      });
      await cleanupPriorPlannerMedia(currentMap.storagePath, currentMap.ownerUid);
      setSelectedMapId(mapId);
      showToast(nextSource.storagePath ? "Map image uploaded to planner media." : "Map image updated locally.");
    },
    [cleanupPriorPlannerMedia, commitProject, project.maps, queueMediaLibrarySync, showToast, uploadPlannerImageSource]
  );

  const handleMapSourceCrop = useCallback(
    async (mapId, croppedAreaPixels) => {
      if (!mapId || !croppedAreaPixels) return;
      const mapItem = project.maps.find((entry) => entry.id === mapId);
      if (!mapItem?.src) return;

      const croppedSrc = await getCroppedImg(mapItem.src, croppedAreaPixels);
      const croppedBlob = dataUrlToBlob(croppedSrc);
      const nextSource = await uploadPlannerImageSource({
        blob: croppedBlob,
        fileName: `${mapItem.name || "map"}-crop.png`,
        contentType: "image/png",
        mediaType: "maps",
        libraryKey: mapItem.libraryKey || mapId,
        localFallbackLabel: "Cropped map image",
      });
      if (!nextSource) return;

      queueMediaLibrarySync();
      commitProject((draft) => {
        const targetMap = draft.maps.find((entry) => entry.id === mapId);
        if (targetMap) {
          targetMap.src = nextSource.src;
          targetMap.storagePath = nextSource.storagePath;
          targetMap.ownerUid = nextSource.ownerUid;
        }
      });
      await cleanupPriorPlannerMedia(mapItem.storagePath, mapItem.ownerUid);
      showToast(nextSource.storagePath ? "Map image cut and uploaded." : "Map image cut and saved locally.");
    },
    [cleanupPriorPlannerMedia, commitProject, project.maps, queueMediaLibrarySync, showToast, uploadPlannerImageSource]
  );

  const handleDeleteMap = useCallback(
    async (mapId) => {
      if (!mapId) return;
      const removedMap = project.maps.find((entry) => entry.id === mapId) || null;
      const mapName = removedMap?.name || "this map";
      if (!confirmMenuAction(`Delete "${mapName}" from the map library?`)) {
        return;
      }

      let fallbackMapId = "";
      queueMediaLibrarySync();
      commitProject((draft) => {
        draft.maps = draft.maps.filter((entry) => entry.id !== mapId);
        if (draft.backgroundMapId === mapId) {
          draft.backgroundMapId = draft.maps[0]?.id || null;
        }
        fallbackMapId = draft.backgroundMapId || draft.maps[0]?.id || "";
      });

      setSelectedMapId((current) => (current === mapId ? fallbackMapId : current));
      setMapSettingsId((current) => (current === mapId ? null : current));
      if (removedMap?.storagePath) {
        await cleanupPriorPlannerMedia(removedMap.storagePath, removedMap.ownerUid);
      }
      showToast("Map deleted.");
    },
    [cleanupPriorPlannerMedia, commitProject, project.maps, queueMediaLibrarySync, showToast]
  );

  const handleMoveMap = useCallback(
    (mapId, direction) => {
      if (!mapId) return;
      queueMediaLibrarySync();
      commitProject((draft) => {
        const currentIndex = draft.maps.findIndex((entry) => entry.id === mapId);
        if (currentIndex === -1) return;
        const nextIndex = direction === "earlier" ? currentIndex - 1 : currentIndex + 1;
        draft.maps = moveArrayItem(draft.maps, currentIndex, nextIndex);
      });
    },
    [commitProject, queueMediaLibrarySync]
  );

  const openMapSettings = useCallback((mapId) => {
    if (!mapId) return;
    setSelectedMapId(mapId);
    setMapSettingsId(mapId);
  }, []);

  const closeMapSettings = useCallback(() => {
    setMapSettingsId(null);
  }, []);

  const handleMapSettingsRename = useCallback(
    (value) => {
      handleMapRename(mapSettingsId, value);
    },
    [handleMapRename, mapSettingsId]
  );

  const handleMapSettingsSourceReplace = useCallback(
    (file) => handleMapSourceReplace(mapSettingsId, file),
    [handleMapSourceReplace, mapSettingsId]
  );

  const handleMapSettingsCrop = useCallback(
    (croppedAreaPixels) => handleMapSourceCrop(mapSettingsId, croppedAreaPixels),
    [handleMapSourceCrop, mapSettingsId]
  );

  const handleMapSettingsMoveEarlier = useCallback(() => {
    handleMoveMap(mapSettingsId, "earlier");
  }, [handleMoveMap, mapSettingsId]);

  const handleMapSettingsMoveLater = useCallback(() => {
    handleMoveMap(mapSettingsId, "later");
  }, [handleMoveMap, mapSettingsId]);

  const handleMapSettingsDelete = useCallback(() => {
    handleDeleteMap(mapSettingsId);
  }, [handleDeleteMap, mapSettingsId]);

  const handleCreateAsset = useCallback(() => {
    let nextAssetId = "";
    const nextLibraryKey = `custom-asset-${uid("library")}`;
    queueMediaLibrarySync();
    commitProject((draft) => {
      nextAssetId = uid("asset");
      draft.assets.push(
        createAssetItem({
          id: nextAssetId,
          libraryKey: nextLibraryKey,
          name: `Main Image ${draft.assets.length + 1}`,
          src: "",
          storagePath: null,
          ownerUid: null,
          subAssets: [],
        })
      );
    });
    setAssetSettingsId(nextAssetId);
  }, [commitProject, queueMediaLibrarySync]);

  const handleAssetRename = useCallback(
    (value) => {
      if (!assetSettingsId) return;
      queueMediaLibrarySync();
      commitProject((draft) => {
        const assetItem = draft.assets.find((entry) => entry.id === assetSettingsId);
        if (assetItem) {
          assetItem.name = value || "Untitled Asset";
        }
      });
    },
    [assetSettingsId, commitProject, queueMediaLibrarySync]
  );

  const handleAssetSourceReplace = useCallback(
    async (file) => {
      if (!file || !assetSettingsId) return;
      const currentAsset = project.assets.find((entry) => entry.id === assetSettingsId);
      if (!currentAsset) return;
      const nextSource = await uploadPlannerImageSource({
        file,
        mediaType: "agents",
        libraryKey: currentAsset.libraryKey || assetSettingsId,
        variantKey: "main",
        fileName: file.name || `${currentAsset.name || "agent-image"}.png`,
        contentType: file.type || "image/png",
        localFallbackLabel: "Agent image",
      });
      if (!nextSource) return;

      queueMediaLibrarySync();
      commitProject((draft) => {
        const assetItem = draft.assets.find((entry) => entry.id === assetSettingsId);
        if (assetItem) {
          assetItem.src = nextSource.src;
          assetItem.storagePath = nextSource.storagePath;
          assetItem.ownerUid = nextSource.ownerUid;
        }
      });
      await cleanupPriorPlannerMedia(currentAsset.storagePath, currentAsset.ownerUid);
      showToast(nextSource.storagePath ? "Agent image uploaded to planner media." : "Agent image updated locally.");
    },
    [assetSettingsId, cleanupPriorPlannerMedia, commitProject, project.assets, queueMediaLibrarySync, showToast, uploadPlannerImageSource]
  );

  const handleAddSubAsset = useCallback(
    async (file) => {
      if (!file || !assetSettingsId) return;
      const currentAsset = project.assets.find((entry) => entry.id === assetSettingsId);
      if (!currentAsset) return;
      const nextSubAssetLibraryKey = `${currentAsset.libraryKey || assetSettingsId}-variant-${uid("variant")}`;
      const nextSource = await uploadPlannerImageSource({
        file,
        mediaType: "agents",
        libraryKey: currentAsset.libraryKey || assetSettingsId,
        variantKey: nextSubAssetLibraryKey,
        fileName: file.name || `${currentAsset.name || "agent-image"}-variant.png`,
        contentType: file.type || "image/png",
        localFallbackLabel: "Agent ability",
      });
      if (!nextSource) return;

      queueMediaLibrarySync();
      commitProject((draft) => {
        const assetItem = draft.assets.find((entry) => entry.id === assetSettingsId);
        if (!assetItem) return;
        assetItem.subAssets.push(
          createSubAssetItem({
            id: uid("sub-asset"),
            libraryKey: nextSubAssetLibraryKey,
            name: `Variant ${assetItem.subAssets.length + 1}`,
            src: nextSource.src,
            storagePath: nextSource.storagePath,
            ownerUid: nextSource.ownerUid,
          })
        );
      });
      showToast(
        nextSource.storagePath
          ? "Agent Ability uploaded to planner media."
          : "Agent Ability added locally."
      );
    },
    [assetSettingsId, commitProject, project.assets, queueMediaLibrarySync, showToast, uploadPlannerImageSource]
  );

  const handleRenameSubAsset = useCallback(
    (subAssetId, value) => {
      if (!assetSettingsId) return;
      queueMediaLibrarySync();
      commitProject((draft) => {
        const assetItem = draft.assets.find((entry) => entry.id === assetSettingsId);
        const subAsset = assetItem?.subAssets.find((entry) => entry.id === subAssetId);
        if (subAsset) {
          subAsset.name = value || "Untitled Variant";
        }
      });
    },
    [assetSettingsId, commitProject, queueMediaLibrarySync]
  );

  const handleReplaceSubAssetSource = useCallback(
    async (subAssetId, file) => {
      if (!file || !assetSettingsId) return;
      const currentAsset = project.assets.find((entry) => entry.id === assetSettingsId);
      const currentSubAsset = currentAsset?.subAssets.find((entry) => entry.id === subAssetId) || null;
      if (!currentAsset || !currentSubAsset) return;
      const nextSource = await uploadPlannerImageSource({
        file,
        mediaType: "agents",
        libraryKey: currentAsset.libraryKey || assetSettingsId,
        variantKey: currentSubAsset.libraryKey || subAssetId,
        fileName: file.name || `${currentSubAsset.name || "agent-ability"}.png`,
        contentType: file.type || "image/png",
        localFallbackLabel: "Agent ability",
      });
      if (!nextSource) return;

      queueMediaLibrarySync();
      commitProject((draft) => {
        const assetItem = draft.assets.find((entry) => entry.id === assetSettingsId);
        const subAsset = assetItem?.subAssets.find((entry) => entry.id === subAssetId);
        if (subAsset) {
          subAsset.src = nextSource.src;
          subAsset.storagePath = nextSource.storagePath;
          subAsset.ownerUid = nextSource.ownerUid;
        }
      });
      await cleanupPriorPlannerMedia(currentSubAsset.storagePath, currentSubAsset.ownerUid);
      showToast(
        nextSource.storagePath
          ? "Agent Ability uploaded to planner media."
          : "Agent Ability updated locally."
      );
    },
    [assetSettingsId, cleanupPriorPlannerMedia, commitProject, project.assets, queueMediaLibrarySync, showToast, uploadPlannerImageSource]
  );

  const handleRemoveSubAsset = useCallback(
    async (subAssetId) => {
      if (!assetSettingsId) return;
      const currentAsset = project.assets.find((entry) => entry.id === assetSettingsId) || null;
      const removedSubAsset = currentAsset?.subAssets.find((entry) => entry.id === subAssetId) || null;
      const assetName = currentAsset?.name || "this image";
      const subAssetName = removedSubAsset?.name || "this Agent Ability";
      if (
        !confirmMenuAction(
          `Delete "${subAssetName}" from "${assetName}"? This also removes it from every sequence in the current animation.`
        )
      ) {
        return;
      }
      queueMediaLibrarySync();
      commitProject((draft) => {
        const assetItem = draft.assets.find((entry) => entry.id === assetSettingsId);
        if (!assetItem) return;
        assetItem.subAssets = assetItem.subAssets.filter((entry) => entry.id !== subAssetId);
        draft.sequences.forEach((sequence) => {
          sequence.items = sequence.items.filter(
            (item) => !(item.assetId === assetSettingsId && item.subAssetId === subAssetId)
          );
        });
      });
      setSelectedItemId((current) => {
        if (!current) return current;
        return activeSequence.items.some(
          (item) =>
            item.instanceId === current &&
            !(item.assetId === assetSettingsId && item.subAssetId === subAssetId)
        )
          ? current
          : null;
      });
      await cleanupPriorPlannerMedia(removedSubAsset?.storagePath, removedSubAsset?.ownerUid);
      showToast("Agent Ability deleted.");
    },
    [
      activeSequence.items,
      assetSettingsId,
      cleanupPriorPlannerMedia,
      commitProject,
      project.assets,
      queueMediaLibrarySync,
      showToast,
    ]
  );

  const handleMoveAsset = useCallback(
    (assetId, direction) => {
      if (!assetId) return;
      queueMediaLibrarySync();
      commitProject((draft) => {
        const currentIndex = draft.assets.findIndex((entry) => entry.id === assetId);
        if (currentIndex === -1) return;
        const nextIndex = direction === "earlier" ? currentIndex - 1 : currentIndex + 1;
        draft.assets = moveArrayItem(draft.assets, currentIndex, nextIndex);
      });
    },
    [commitProject, queueMediaLibrarySync]
  );

  const handleDeleteAsset = useCallback(
    async (assetId) => {
      if (!assetId) return;
      const removedAsset = project.assets.find((entry) => entry.id === assetId) || null;
      const assetName = removedAsset?.name || "this agent image";
      if (
        !confirmMenuAction(
          `Delete "${assetName}" from Agent Abilities? This also removes it from every sequence in the current animation.`
        )
      ) {
        return;
      }

      queueMediaLibrarySync();
      commitProject((draft) => {
        draft.assets = draft.assets.filter((entry) => entry.id !== assetId);
        draft.sequences.forEach((sequence) => {
          sequence.items = sequence.items.filter((item) => item.assetId !== assetId);
        });
      });

      setSelectedItemId((current) => {
        if (!current) return current;
        return activeSequence.items.some((item) => item.instanceId === current && item.assetId !== assetId)
          ? current
          : null;
      });
      setAssetSettingsId((current) => (current === assetId ? null : current));
      await cleanupPriorPlannerMedia(removedAsset?.storagePath, removedAsset?.ownerUid);
      for (const subAsset of removedAsset?.subAssets || []) {
        // Best-effort cleanup for old remote variant files.
        // Running sequentially keeps deletes deterministic in browser dev tools.
        // eslint-disable-next-line no-await-in-loop
        await cleanupPriorPlannerMedia(subAsset.storagePath, subAsset.ownerUid);
      }
      showToast("Agent image deleted.");
    },
    [activeSequence.items, cleanupPriorPlannerMedia, commitProject, project.assets, queueMediaLibrarySync, showToast]
  );

  const handleAssetMoveEarlier = useCallback(() => {
    handleMoveAsset(assetSettingsId, "earlier");
  }, [assetSettingsId, handleMoveAsset]);

  const handleAssetMoveLater = useCallback(() => {
    handleMoveAsset(assetSettingsId, "later");
  }, [assetSettingsId, handleMoveAsset]);

  const clearCurrentSequence = useCallback(() => {
    if (!confirmMenuAction("Delete everything in the current step?")) {
      return;
    }
    commitProject((draft) => {
      const sequence = draft.sequences[activeSequenceIndex];
      if (!sequence) return;
      sequence.edited = true;
      sequence.items = [];
    });
    setSelectedItemId(null);
  }, [activeSequenceIndex, commitProject]);

  const resetCurrentSequenceToInherited = useCallback(() => {
    if (!confirmMenuAction("Delete the custom edits on this step and revert it to the inherited version?")) {
      return;
    }
    commitProject((draft) => {
      const sequence = draft.sequences[activeSequenceIndex];
      if (!sequence) return;
      sequence.edited = false;
      sequence.items = [];
    });
    setSelectedItemId(null);
    showToast(`Sequence ${sequenceLabel(activeSequenceIndex)} now inherits the latest earlier step again.`);
  }, [activeSequenceIndex, commitProject, showToast]);

  const handleClearAnimation = useCallback(() => {
    if (!confirmMenuAction("Clear the entire animation and delete all sequence content?")) {
      return;
    }
    commitProject((draft) => {
      draft.sequences = draft.sequences.map((sequence, index) => ({
        ...createEmptySequence(index),
        id: sequence.id || `sequence-${index + 1}`,
        name: sequence.name || `Sequence ${index + 1}`,
      }));
    });
    setSelectedItemId(null);
    setActiveSequenceIndex(0);
    setIsPlayingPreview(false);
    setPreviewRenderState(null);
    showToast("All sequence steps were cleared. Your map and uploaded libraries were kept.");
  }, [commitProject, showToast]);

  const moveLayer = useCallback(
    (direction) => {
      if (!selectedItem) return;
      updateSelectedItem((item, draft) => {
        const sequence = ensureSequenceEditable(draft, activeSequenceIndex);
        if (!sequence) return;
        const sequenceItems = sequence.items;
        const sorted = [...sequenceItems].sort((left, right) => left.zIndex - right.zIndex);
        const currentIndex = sorted.findIndex((entry) => entry.instanceId === item.instanceId);
        if (currentIndex === -1) return;

        if (direction === "front") {
          item.zIndex = sorted.reduce((highest, entry) => Math.max(highest, entry.zIndex), 0) + 1;
          return;
        }
        if (direction === "back") {
          item.zIndex = sorted.reduce((lowest, entry) => Math.min(lowest, entry.zIndex), 0) - 1;
          return;
        }

        const neighborIndex = direction === "up" ? currentIndex + 1 : currentIndex - 1;
        const neighbor = sorted[neighborIndex];
        if (!neighbor) return;

        const currentZ = item.zIndex;
        item.zIndex = neighbor.zIndex;
        const actualNeighbor = sequenceItems.find((entry) => entry.instanceId === neighbor.instanceId);
        if (actualNeighbor) {
          actualNeighbor.zIndex = currentZ;
        }
      });
    },
    [activeSequenceIndex, selectedItem, updateSelectedItem]
  );

  const handleDeleteSelectedItem = useCallback(() => {
    if (!selectedItemId) return;
    commitProject((draft) => {
      const sequence = ensureSequenceEditable(draft, activeSequenceIndex);
      if (!sequence) return;
      sequence.items = sequence.items.filter((item) => item.instanceId !== selectedItemId);
    });
    setSelectedItemId(null);
  }, [activeSequenceIndex, commitProject, selectedItemId]);

  const getCanvasPoint = useCallback(
    (clientX, clientY) => {
      const stageElement = stageRef.current;
      if (!stageElement) return null;

      const rect = stageElement.getBoundingClientRect();
      const scaleX = project.canvas.width / Math.max(rect.width, 1);
      const scaleY = project.canvas.height / Math.max(rect.height, 1);
      return {
        x: (clientX - rect.left) * scaleX,
        y: (clientY - rect.top) * scaleY,
      };
    },
    [project.canvas.height, project.canvas.width]
  );

  const beginPointerAction = useCallback(
    (event, item, mode, direction = null) => {
      if (isPlayingPreview) return;
      const pointer = getCanvasPoint(event.clientX, event.clientY);
      if (!pointer) return;

      event.preventDefault();
      event.stopPropagation();
      setSelectedItemId(item.instanceId);

      const rotationCenterX = item.x + item.width / 2;
      const rotationCenterY = item.y + item.height / 2;

      interactionRef.current = {
        itemId: item.instanceId,
        mode,
        direction,
        startPointer: pointer,
        pointerOffsetX: pointer.x - item.x,
        pointerOffsetY: pointer.y - item.y,
        rotationCenterX,
        rotationCenterY,
        startPointerAngle: Math.atan2(pointer.y - rotationCenterY, pointer.x - rotationCenterX),
        startRotation: item.rotation,
        startItem: { ...item },
      };
    },
    [getCanvasPoint, isPlayingPreview]
  );

  useEffect(() => {
    const handlePointerMove = (event) => {
      const interaction = interactionRef.current;
      if (!interaction) return;

      const pointer = getCanvasPoint(event.clientX, event.clientY);
      if (!pointer) return;

      const deltaX = pointer.x - interaction.startPointer.x;
      const deltaY = pointer.y - interaction.startPointer.y;

      commitProject((draft) => {
        const sequence = ensureSequenceEditable(draft, activeSequenceIndex);
        if (!sequence) return;
        const item = sequence.items.find((entry) => entry.instanceId === interaction.itemId);
        if (!item) return;

        if (interaction.mode === "drag") {
          item.x = pointer.x - interaction.pointerOffsetX;
          item.y = pointer.y - interaction.pointerOffsetY;
        }

        if (interaction.mode === "resize") {
          const resized = buildResizeResult(
            interaction.startItem,
            interaction.direction,
            deltaX,
            deltaY
          );
          item.x = resized.x;
          item.y = resized.y;
          item.width = resized.width;
          item.height = resized.height;
        }

        if (interaction.mode === "rotate") {
          const currentAngle = Math.atan2(
            pointer.y - interaction.rotationCenterY,
            pointer.x - interaction.rotationCenterX
          );
          let deltaAngle = currentAngle - interaction.startPointerAngle;
          while (deltaAngle > Math.PI) deltaAngle -= Math.PI * 2;
          while (deltaAngle < -Math.PI) deltaAngle += Math.PI * 2;
          item.rotation = normalizeAngle(
            interaction.startRotation + (deltaAngle * 180) / Math.PI
          );
        }
      });
    };

    const handlePointerUp = () => {
      interactionRef.current = null;
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [activeSequenceIndex, commitProject, getCanvasPoint]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (isPlayingPreview || !selectedItemId || isEditableTag(document.activeElement)) {
        return;
      }

      if (event.key === "Delete" || event.key === "Backspace") {
        event.preventDefault();
        handleDeleteSelectedItem();
        return;
      }

      if (event.key === "h" || event.key === "H") {
        event.preventDefault();
        updateSelectedItem((item) => {
          item.flipX = !item.flipX;
        });
        return;
      }

      if (event.key === "v" || event.key === "V") {
        event.preventDefault();
        updateSelectedItem((item) => {
          item.flipY = !item.flipY;
        });
        return;
      }

      if (event.key === "q" || event.key === "Q") {
        event.preventDefault();
        updateSelectedItem((item) => {
          item.rotation = normalizeAngle(item.rotation - 45);
        });
        return;
      }

      if (event.key === "e" || event.key === "E") {
        event.preventDefault();
        updateSelectedItem((item) => {
          item.rotation = normalizeAngle(item.rotation + 45);
        });
        return;
      }

      const nudgeAmount = event.shiftKey ? 10 : 1;
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        updateSelectedItem((item) => {
          item.x -= nudgeAmount;
        });
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        updateSelectedItem((item) => {
          item.x += nudgeAmount;
        });
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        updateSelectedItem((item) => {
          item.y -= nudgeAmount;
        });
      }
      if (event.key === "ArrowDown") {
        event.preventDefault();
        updateSelectedItem((item) => {
          item.y += nudgeAmount;
        });
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleDeleteSelectedItem, isPlayingPreview, selectedItemId, updateSelectedItem]);

  const togglePreview = useCallback(() => {
    if (isPlayingPreview) {
      setIsPlayingPreview(false);
      setPreviewRenderState(null);
      if (previewFrameRef.current) {
        window.cancelAnimationFrame(previewFrameRef.current);
      }
      return;
    }

    previewLastSelectionRef.current = activeSequenceIndex;
    previewStartedAtRef.current = null;
    setIsPlayingPreview(true);
  }, [activeSequenceIndex, isPlayingPreview]);

  useEffect(() => {
    if (!isPlayingPreview) return undefined;

    const totalDuration = getTimelineDuration(project);

    const tick = (timestamp) => {
      if (previewStartedAtRef.current == null) {
        previewStartedAtRef.current = timestamp;
      }

      const playbackRate = project.exportSettings.playbackRate || 1;
      const elapsed = (timestamp - previewStartedAtRef.current) * playbackRate;

      if (elapsed >= totalDuration) {
        const timelineEntries = getTimelineSequenceEntries(project);
        setPreviewRenderState(
          getStaticRenderState(project, timelineEntries[timelineEntries.length - 1]?.sequenceIndex || 0)
        );
        setIsPlayingPreview(false);
        previewFrameRef.current = null;
        previewStartedAtRef.current = null;
        return;
      }

      setPreviewRenderState(getPlaybackRenderState(project, elapsed));
      previewFrameRef.current = window.requestAnimationFrame(tick);
    };

    previewFrameRef.current = window.requestAnimationFrame(tick);
    return () => {
      if (previewFrameRef.current) {
        window.cancelAnimationFrame(previewFrameRef.current);
        previewFrameRef.current = null;
      }
      previewStartedAtRef.current = null;
    };
  }, [isPlayingPreview, project]);

  const handleSaveSnapshot = useCallback(async () => {
    const nextProjectName = String(project.projectName || "").trim();
    if (!nextProjectName) {
      showToast("Enter an animation project name before saving.");
      return;
    }

    const shouldOverwrite =
      selectedSavedProjectId &&
      selectedSavedProject &&
      selectedSavedProject.projectName === nextProjectName;
    const snapshot = await saveProjectSnapshot(
      shouldOverwrite ? selectedSavedProjectId : "",
      nextProjectName,
      {
        ...project,
        projectName: nextProjectName,
      }
    );
    setSelectedSavedProjectId(snapshot.id);
    await refreshSavedProjects();
    showToast("Animation point saved.");
  }, [project, refreshSavedProjects, selectedSavedProject, selectedSavedProjectId, showToast]);

  const handleLoadSnapshotById = useCallback(
    async (projectId) => {
      if (!projectId) {
        showToast("Choose a saved animation point first.");
        return;
      }

      setSelectedSavedProjectId(projectId);
      const [loaded, mediaLibrary] = await Promise.all([loadProjectSnapshot(projectId), loadMediaLibrary()]);
      if (!loaded) {
        showToast("That saved animation point could not be found.");
        return;
      }

      const hydratedProject = hydrateProjectFromMediaLibrary(loaded, mediaLibrary);
      setProject(hydratedProject);
      setActiveSequenceIndex(0);
      setSelectedItemId(null);
      setAssetSettingsId(null);
      setMapSettingsId(null);
      setSelectedMapId(hydratedProject.backgroundMapId || hydratedProject.maps[0]?.id || "");
      showToast("Animation point loaded.");
    },
    [showToast]
  );

  const handleDeleteSnapshot = useCallback(async (projectId) => {
    if (!projectId) {
      showToast("Choose a saved animation point to delete.");
      return;
    }
    const snapshotName =
      savedProjects.find((savedProject) => savedProject.id === projectId)?.projectName || "this animation point";
    if (!confirmMenuAction(`Delete "${snapshotName}"?`)) {
      return;
    }
    await deleteProjectSnapshot(projectId);
    setSelectedSavedProjectId((current) => (current === projectId ? "" : current));
    await refreshSavedProjects();
    showToast("Animation point deleted.");
  }, [refreshSavedProjects, savedProjects, showToast]);

  const renderAnimationVideoBlob = useCallback(async () => {
    const canvas = document.createElement("canvas");
    canvas.width = project.canvas.width;
    canvas.height = project.canvas.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("This browser could not create an export canvas.");
    }

    if (typeof MediaRecorder === "undefined") {
      throw new Error("This browser does not support video export with MediaRecorder.");
    }

    const exportPlan = getAnimationExportPlan(project);
    const stream = canvas.captureStream(exportPlan.fps);
    const mimeTypes = [
      "video/webm;codecs=vp9",
      "video/webm;codecs=vp8",
      "video/webm",
    ];
    const mimeType = mimeTypes.find((candidate) => MediaRecorder.isTypeSupported(candidate)) || "";
    const chunks = [];
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);

    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        chunks.push(event.data);
      }
    };

    const stopped = new Promise((resolve) => {
      recorder.onstop = () => {
        resolve(
          new Blob(chunks, {
            type: mimeType || "video/webm",
          })
        );
      };
    });

    recorder.start();

    for (let frameIndex = 0; frameIndex < exportPlan.totalFrames; frameIndex += 1) {
      const elapsedMs = Math.min(
        frameIndex * exportPlan.timelineFrameStepMs,
        exportPlan.timelineDurationMs
      );
      const renderState = getPlaybackRenderState(project, elapsedMs);
      await drawRenderStateToCanvas(ctx, project, renderState, imageCacheRef.current);
      // MediaRecorder needs real time to ingest the frame stream.
      // Keeping this tied to fps makes preview and export line up visually.
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => window.setTimeout(resolve, exportPlan.outputFrameIntervalMs));
    }

    recorder.stop();
    const rawBlob = await stopped;
    const fixedBlob = await new Promise((resolve) => {
      try {
        fixWebmDuration(rawBlob, exportPlan.outputDurationMs, resolve);
      } catch (error) {
        console.warn("Strats planner: failed to patch WebM duration metadata", error);
        resolve(rawBlob);
      }
    });

    return {
      blob: fixedBlob,
      mimeType: fixedBlob.type || mimeType || "video/webm",
      durationMs: exportPlan.outputDurationMs,
      width: project.canvas.width,
      height: project.canvas.height,
    };
  }, [project]);

  const renderAnimationGifBlob = useCallback(async () => {
    const canvas = document.createElement("canvas");
    canvas.width = project.canvas.width;
    canvas.height = project.canvas.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("This browser could not create an export canvas.");
    }

    const exportPlan = getAnimationExportPlan(project);
    const gif = GIFEncoder();
    const frameDelays = buildGifFrameDelayList(exportPlan.totalFrames, exportPlan.outputDurationMs);

    for (let frameIndex = 0; frameIndex < exportPlan.totalFrames; frameIndex += 1) {
      const elapsedMs = Math.min(
        frameIndex * exportPlan.timelineFrameStepMs,
        exportPlan.timelineDurationMs
      );
      const renderState = getPlaybackRenderState(project, elapsedMs);
      await drawRenderStateToCanvas(ctx, project, renderState, imageCacheRef.current);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const palette = quantize(imageData.data, GIF_MAX_COLORS, {
        format: GIF_COLOR_FORMAT,
      });
      const indexedFrame = applyPalette(imageData.data, palette, GIF_COLOR_FORMAT);

      gif.writeFrame(indexedFrame, canvas.width, canvas.height, {
        palette,
        repeat: 0,
        delay: frameDelays[frameIndex],
      });

      // Yield between frames so long GIF exports do not hard-freeze the page.
      if (frameIndex < exportPlan.totalFrames - 1) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => window.setTimeout(resolve, 0));
      }
    }

    gif.finish();

    return {
      blob: new Blob([gif.bytesView()], { type: "image/gif" }),
      mimeType: "image/gif",
      durationMs: exportPlan.outputDurationMs,
      width: canvas.width,
      height: canvas.height,
    };
  }, [project]);

  const handlePublishToGuide = useCallback(async () => {
    if (!selectedGuidePublishSlot) {
      showToast("Choose a guide publish slot first.");
      return;
    }

    if (!userData?.uid) {
      showToast("Guide publishing requires a signed-in admin Firebase account.");
      return;
    }

    if (
      selectedGuidePublishAsset?.remoteUrl &&
      !confirmMenuAction(
        `Overwrite the published guide media for "${selectedGuidePublishSlot.mediaLabel}" in "${selectedGuidePublishSlot.sectionLabel}"?`
      )
    ) {
      return;
    }

    setPublishingGuide(true);

    try {
      const fileBaseName = sanitizeFileName(
        project.exportSettings.fileBaseName ||
          project.projectName ||
          selectedGuidePublishSlot.mediaKey ||
          DEFAULT_FILE_BASENAME
      );
      const renderResult = await renderAnimationVideoBlob();
      const stagingStoragePath = buildGuidePublishStagingStoragePath(userData.uid, fileBaseName);

      await uploadBytes(storageRef(storage, stagingStoragePath), renderResult.blob, {
        contentType: renderResult.mimeType || "video/webm",
        cacheControl: "private, max-age=0, no-store",
      });

      const callable = httpsCallable(
        firebaseFunctions,
        FUNCTION_NAMES.publishPlannerGuideMedia
      );

      await callable({
        slotId: selectedGuidePublishSlot.id,
        stagingStoragePath,
        mimeType: renderResult.mimeType,
        durationMs: renderResult.durationMs,
        width: renderResult.width,
        height: renderResult.height,
      });

      await ensureGuideMediaManifestLoaded({ force: true });
      showToast(
        `Published to ${selectedGuidePublishSlot.routeTitle} -> ${selectedGuidePublishSlot.sectionLabel} -> ${selectedGuidePublishSlot.mediaLabel}.`
      );
    } catch (error) {
      console.error("Strats planner: guide publish failed", error);
      showToast(error?.message || "Guide publish failed. Check the browser console for details.");
    } finally {
      setPublishingGuide(false);
    }
  }, [
    project.exportSettings.fileBaseName,
    project.projectName,
    renderAnimationVideoBlob,
    selectedGuidePublishAsset?.remoteUrl,
    selectedGuidePublishSlot,
    showToast,
    userData?.uid,
  ]);

  const handleExport = useCallback(async () => {
    const mode = project.exportSettings.exportMode;
    const fileBaseName = sanitizeFileName(project.exportSettings.fileBaseName || project.projectName);
    const canvas = document.createElement("canvas");
    canvas.width = project.canvas.width;
    canvas.height = project.canvas.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      showToast("This browser could not create an export canvas.");
      return;
    }

    setExporting(true);

    try {
      if (mode === "json") {
        const blob = new Blob([JSON.stringify(project, null, 2)], {
          type: "application/json",
        });
        downloadBlob(blob, `${fileBaseName}.json`);
        showToast("Project JSON exported.");
        return;
      }

      if (mode === "png") {
        const renderState = getStaticRenderState(project, activeSequenceIndex);
        await drawRenderStateToCanvas(ctx, project, renderState, imageCacheRef.current);
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
        if (!blob) {
          throw new Error("PNG export failed.");
        }
        downloadBlob(blob, `${fileBaseName}-sequence-${activeSequenceIndex + 1}.png`);
        showToast("PNG snapshot exported.");
        return;
      }

      if (mode === "gif") {
        const renderResult = await renderAnimationGifBlob();
        downloadBlob(renderResult.blob, `${fileBaseName}.gif`);
        showToast("GIF export completed.");
        return;
      }

      const renderResult = await renderAnimationVideoBlob();
      downloadBlob(renderResult.blob, `${fileBaseName}.webm`);
      showToast("WebM export completed.");
    } catch (error) {
      console.error("Strats planner: export failed", error);
      showToast(error?.message || "Export failed. Check the browser console for details.");
    } finally {
      setExporting(false);
    }
  }, [activeSequenceIndex, project, renderAnimationGifBlob, renderAnimationVideoBlob, showToast]);

  if (!gateChecked) {
    return <div className="StratsPlannerGate StratsPlannerGate--loading">Checking access…</div>;
  }

  if (!isUnlocked) {
    return (
      <section className="StratsPlannerGate">
        <div className="StratsPlannerGate__card">
          <div className="StratsPlannerPanel__eyebrow">Admin Access</div>
          <h1>Strats Planner</h1>
          <p>
            This page is protected by a planner password. This is a client-side gate for workflow control,
            not true server-side security.
          </p>

          <label className="StratsPlannerField">
            <span>Password</span>
            <input
              type="password"
              value={passwordInput}
              onChange={(event) => setPasswordInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  handleUnlock();
                }
              }}
              autoComplete="current-password"
            />
          </label>

          {passwordError ? <div className="StratsPlannerGate__error">{passwordError}</div> : null}

          <button type="button" className="StratsPlannerButton" onClick={handleUnlock}>
            Unlock Planner
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="StratsPlannerPage">
      <div className="StratsPlannerPage__header">
        <div>
          <div className="StratsPlannerPanel__eyebrow">Valovate Guide Admin Tool</div>
          <h1>Strats Planner</h1>
        </div>
      </div>

      <div className="StratsPlannerLayout">
        <aside className="StratsPlannerRail StratsPlannerRail--left">
          <div className="StratsPlannerPanel StratsPlannerPanel--maps">
            <button
              type="button"
              className="StratsPlannerPanel__toggle"
              onClick={() => setIsMapPanelOpen((current) => !current)}
            >
              {isMapPanelOpen ? "Hide map selection" : "Select Map"}
            </button>

            {isMapPanelOpen ? (
              <div className="StratsPlannerMapShelf">
                <div className="StratsPlannerPanel__eyebrow">Canvas Background Maps</div>
                <div className="StratsPlannerMapGrid">
                  {project.maps.map((mapItem) => {
                    const isActiveBackground = project.backgroundMapId === mapItem.id;
                    return (
                      <div
                        key={mapItem.id}
                        className={`StratsPlannerMapCard ${
                          isActiveBackground ? "is-active" : ""
                        } ${selectedMapId === mapItem.id ? "is-selected" : ""}`}
                      >
                        <button
                          type="button"
                          className="StratsPlannerMapCard__tile"
                          onClick={() => handleMapSelect(mapItem.id)}
                        >
                          <div className="StratsPlannerThumb StratsPlannerThumb--mapPreview">
                            {mapItem.src ? <img src={mapItem.src} alt={mapItem.name} /> : <span>{mapItem.name}</span>}
                          </div>
                          <span>{mapItem.name}</span>
                        </button>

                        <button
                          type="button"
                          className="StratsPlannerMapCard__settings"
                          aria-label={`Open settings for ${mapItem.name}`}
                          title={`Open settings for ${mapItem.name}`}
                          onClick={() => openMapSettings(mapItem.id)}
                        >
                          <IconSettings />
                        </button>
                      </div>
                    );
                  })}

                  <button
                    type="button"
                    className="StratsPlannerMapCard StratsPlannerMapCard--add"
                    onClick={handleAddMap}
                  >
                    + Add map
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          <div className="StratsPlannerPanel StratsPlannerPanel--sequence">
            <div className="StratsPlannerPanel__header">
              <div className="StratsPlannerPanel__eyebrow">Sequence Selector</div>
              <div className="StratsPlannerIconRow">
                <PlannerIconButton
                  label={isPlayingPreview ? "Stop playback preview" : "Play animation preview"}
                  onClick={togglePreview}
                  className={isPlayingPreview ? "is-active" : ""}
                >
                  <IconPlay />
                </PlannerIconButton>
                <PlannerIconButton
                  label="Reset current step to inherited state"
                  onClick={resetCurrentSequenceToInherited}
                >
                  <IconReset />
                </PlannerIconButton>
                <PlannerIconButton
                  label="Delete everything on the current step"
                  onClick={clearCurrentSequence}
                  className="StratsPlannerIconButton--dangerGlyph"
                >
                  <IconStepClear />
                </PlannerIconButton>
                <PlannerIconButton
                  label="Clear the entire animation"
                  onClick={handleClearAnimation}
                  className="StratsPlannerIconButton--dangerGlyph"
                >
                  <IconClearAll />
                </PlannerIconButton>
              </div>
            </div>
            <div className="StratsPlannerSequenceGrid">
              {project.sequences.map((sequence, index) => {
                const effectiveSequence = getEffectiveSequenceState(project, index);
                const hasContent = effectiveSequence.items.length > 0;
                return (
                  <button
                    key={sequence.id}
                    type="button"
                    className={`StratsPlannerSequenceTile ${
                      activeSequenceIndex === index ? "is-active" : ""
                    } ${hasContent ? "has-content" : ""} ${sequence.edited ? "is-step" : "is-inherited"}`}
                    onClick={() => setActiveSequenceIndex(index)}
                  >
                    <span className="StratsPlannerSequenceTile__label">{sequenceLabel(index)}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="StratsPlannerPanel StratsPlannerPanel--assets">
            <div className="StratsPlannerPanel__header">
              <div>
                <div className="StratsPlannerPanel__eyebrow">Image Library</div>
                <h2>Agent Abilities</h2>
              </div>
              <div className="StratsPlannerPanel__headerActions">
                <button
                  type="button"
                  className={`StratsPlannerButton StratsPlannerButton--ghost StratsPlannerButton--compact ${
                    highlightUsedAssetsOnly ? "is-active" : ""
                  }`}
                  onClick={() => setHighlightUsedAssetsOnly((current) => !current)}
                  aria-pressed={highlightUsedAssetsOnly}
                >
                  <IconFilter />
                  <span>Used</span>
                </button>
                <button
                  type="button"
                  className="StratsPlannerButton StratsPlannerButton--ghost StratsPlannerButton--compact StratsPlannerButton--iconOnly"
                  onClick={handleCreateAsset}
                  aria-label="Add agent image"
                  title="Add agent image"
                >
                  +
                </button>
              </div>
            </div>

            <div className="StratsPlannerAssetGrid">
              {visibleAssetItems.length === 0 ? (
                <div className="StratsPlannerEmpty StratsPlannerAssetGrid__empty">
                  No agent images are currently used in this animation.
                </div>
              ) : (
                visibleAssetItems.map((assetItem) => {
                  const hasAbilities = assetItem.subAssets.length > 0;
                  return (
                    <div key={assetItem.id} className="StratsPlannerAssetCard">
                      <button
                        type="button"
                        className="StratsPlannerAssetCard__settings"
                        onClick={() => setAssetSettingsId(assetItem.id)}
                        aria-label={`Open settings for ${assetItem.name}`}
                        title={`Open settings for ${assetItem.name}`}
                      >
                        <IconSettings />
                      </button>

                      <button
                        type="button"
                        className="StratsPlannerAssetCard__tile"
                        onClick={() => addAssetToCurrentSequence(assetItem.id)}
                      >
                        <div className="StratsPlannerThumb">
                          {assetItem.src ? <img src={assetItem.src} alt={assetItem.name} /> : <span>Empty</span>}
                        </div>
                        <span>{assetItem.name}</span>
                      </button>

                      {hasAbilities ? (
                        <div className="StratsPlannerAssetCard__abilities">
                          <div className="StratsPlannerAssetCard__abilitiesTitle">Agent Abilities</div>
                          <div className="StratsPlannerAssetCard__abilitiesGrid">
                            {assetItem.subAssets.map((subAsset) => (
                              <button
                                key={subAsset.id}
                                type="button"
                                className="StratsPlannerAbilityButton"
                                onClick={() => addAssetToCurrentSequence(assetItem.id, subAsset.id)}
                              >
                                <div className="StratsPlannerThumb">
                                  {subAsset.src ? <img src={subAsset.src} alt={subAsset.name} /> : <span>Empty</span>}
                                </div>
                                <span>{subAsset.name}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </aside>

        <div className="StratsPlannerCanvasPane">
          <div className="StratsPlannerCanvasShell">
            <div
              className="StratsPlannerCanvas"
              ref={stageRef}
              onPointerDown={() => {
                if (!isPlayingPreview) {
                  setSelectedItemId(null);
                }
              }}
            >
              <div className="StratsPlannerCanvas__paper" />

              {currentRenderState.fromMapId ? (
                <img
                  className="StratsPlannerCanvas__map"
                  src={mapLookup.get(currentRenderState.fromMapId)?.src || ""}
                  alt=""
                  style={{ opacity: currentRenderState.toMapId !== currentRenderState.fromMapId ? 1 - currentRenderState.mapMix : 1 }}
                />
              ) : null}

              {currentRenderState.toMapId &&
              currentRenderState.toMapId !== currentRenderState.fromMapId &&
              mapLookup.get(currentRenderState.toMapId)?.src ? (
                <img
                  className="StratsPlannerCanvas__map"
                  src={mapLookup.get(currentRenderState.toMapId)?.src || ""}
                  alt=""
                  style={{ opacity: currentRenderState.mapMix }}
                />
              ) : null}

              {!mapLookup.get(project.backgroundMapId)?.src && !isPlayingPreview ? (
                <div className="StratsPlannerCanvas__emptyState">
                  Pick or upload a map background from the map shelf, then add agent images or abilities from the left rail.
                </div>
              ) : null}

              {currentRenderState.items.map((item) => {
                const itemMedia = getSequenceItemMedia(assetLookup, item);
                if (!itemMedia?.src) return null;
                const isSelected = selectedItemId === item.instanceId && !isPlayingPreview;
                return (
                  <div
                    key={item.instanceId}
                    className={`StratsPlannerCanvasItem ${isSelected ? "is-selected" : ""}`}
                    style={{
                      left: `${(item.x / project.canvas.width) * 100}%`,
                      top: `${(item.y / project.canvas.height) * 100}%`,
                      width: `${(item.width / project.canvas.width) * 100}%`,
                      height: `${(item.height / project.canvas.height) * 100}%`,
                      zIndex: item.zIndex,
                      opacity: item.opacity ?? 1,
                      transform: `rotate(${item.rotation}deg) scale(${item.flipX ? -1 : 1}, ${
                        item.flipY ? -1 : 1
                      })`,
                    }}
                    onPointerDown={(event) => beginPointerAction(event, item, "drag")}
                    onClick={(event) => {
                      event.stopPropagation();
                      if (!isPlayingPreview) {
                        setSelectedItemId(item.instanceId);
                      }
                    }}
                  >
                    <img src={itemMedia.src} alt={itemMedia.label} draggable="false" />

                    {isSelected ? (
                      <>
                        <div className="StratsPlannerRotateGuide" aria-hidden="true" />
                        <button
                          type="button"
                          className="StratsPlannerRotateHandle"
                          onPointerDown={(event) => beginPointerAction(event, item, "rotate")}
                          aria-label="Rotate image"
                        />
                        {["nw", "n", "ne", "e", "se", "s", "sw", "w"].map((direction) => (
                          <button
                            key={direction}
                            type="button"
                            className={`StratsPlannerHandle StratsPlannerHandle--${direction}`}
                            onPointerDown={(event) => beginPointerAction(event, item, "resize", direction)}
                            aria-label={`Resize ${direction}`}
                          />
                        ))}
                      </>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <aside className="StratsPlannerRail StratsPlannerRail--right">
          <div className="StratsPlannerPanel StratsPlannerPanel--savePoints">
            <div className="StratsPlannerPanel__header">
              <div className="StratsPlannerPanel__eyebrow">Save Animation Point</div>
            </div>

            <div className="StratsPlannerSavePointComposer">
              <input
                type="text"
                value={project.projectName}
                placeholder="Enter Animation Project Name here"
                onChange={(event) =>
                  commitProject((draft) => {
                    draft.projectName = event.target.value;
                  })
                }
              />
              <button type="button" className="StratsPlannerButton" onClick={handleSaveSnapshot}>
                Save
              </button>
            </div>

            <div className="StratsPlannerSavePointList">
              {savedProjects.length === 0 ? (
                <div className="StratsPlannerEmpty">No saved animation points yet.</div>
              ) : (
                savedProjects.map((savedProject) => (
                  <div
                    key={savedProject.id}
                    className={`StratsPlannerSavePointRow ${
                      selectedSavedProjectId === savedProject.id ? "is-active" : ""
                    }`}
                  >
                    <span className="StratsPlannerSavePointRow__name">{savedProject.projectName}</span>
                    <div className="StratsPlannerSavePointRow__actions">
                      <PlannerIconButton
                        label={`Load ${savedProject.projectName}`}
                        onClick={() => handleLoadSnapshotById(savedProject.id)}
                      >
                        <IconLoad />
                      </PlannerIconButton>
                      <PlannerIconButton
                        label={`Delete ${savedProject.projectName}`}
                        onClick={() => handleDeleteSnapshot(savedProject.id)}
                        className="StratsPlannerIconButton--dangerGlyph"
                      >
                        <IconTrash />
                      </PlannerIconButton>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="StratsPlannerPanel StratsPlannerPanel--export">
            <div className="StratsPlannerPanel__header">
              <div className="StratsPlannerPanel__eyebrow">Export Menu</div>
              <div className="StratsPlannerPanel__headerActions">
                <div className="StratsPlannerHoverHelp">
                  <PlannerIconButton label="Show planner shortcuts">
                    <IconHelp />
                  </PlannerIconButton>
                  <div className="StratsPlannerHoverHelp__card" role="tooltip">
                    <strong>Shortcuts</strong>
                    <span>Arrows: move selected image by 1px</span>
                    <span>Shift + Arrows: move selected image by 10px</span>
                    <span>Q / E: rotate selected image by 45 degrees</span>
                    <span>H / V: flip selected image horizontally or vertically</span>
                    <span>Delete / Backspace: remove selected image from the current step</span>
                  </div>
                </div>
                <PlannerIconButton
                  label={isExportMenuExpanded ? "Collapse export menu" : "Expand export menu"}
                  onClick={() => setIsExportMenuExpanded((current) => !current)}
                >
                  {isExportMenuExpanded ? <IconChevronUp /> : <IconChevronDown />}
                </PlannerIconButton>
              </div>
            </div>

            {isExportMenuExpanded ? (
              <>
            <div className="StratsPlannerCompactGrid">
              <label className="StratsPlannerField">
                <span>Export type</span>
                <select
                  value={project.exportSettings.exportMode}
                  onChange={(event) =>
                    commitProject((draft) => {
                      draft.exportSettings.exportMode = event.target.value;
                    })
                  }
                >
                  <option value="video">WEBM</option>
                  <option value="gif">GIF</option>
                  <option value="png">PNG snapshot</option>
                  <option value="json">Project JSON</option>
                </select>
              </label>
            </div>

            <div className="StratsPlannerCompactGrid">
              <label className="StratsPlannerField">
                <span>FPS</span>
                <input
                  type="number"
                  min="1"
                  max="60"
                  value={project.exportSettings.fps}
                  onChange={(event) =>
                    commitProject((draft) => {
                      draft.exportSettings.fps = sanitizeNumber(event.target.value, DEFAULT_FPS);
                    })
                  }
                />
              </label>

              <label className="StratsPlannerField">
                <span>Playback rate</span>
                <input
                  type="number"
                  min="0.1"
                  max="4"
                  step="0.1"
                  value={project.exportSettings.playbackRate}
                  onChange={(event) =>
                    commitProject((draft) => {
                      draft.exportSettings.playbackRate = sanitizeNumber(
                        event.target.value,
                        DEFAULT_PLAYBACK_RATE
                      );
                    })
                  }
                />
              </label>
            </div>

            <div className="StratsPlannerCompactGrid">
              <label className="StratsPlannerField">
                <span>Hold per sequence (ms)</span>
                <input
                  type="number"
                  min="100"
                  step="50"
                  value={project.exportSettings.frameHoldMs}
                  onChange={(event) =>
                    commitProject((draft) => {
                      draft.exportSettings.frameHoldMs = sanitizeNumber(
                        event.target.value,
                        DEFAULT_FRAME_HOLD_MS
                      );
                    })
                  }
                />
              </label>

              <label className="StratsPlannerField">
                <span>Transition speed (ms)</span>
                <input
                  type="number"
                  min="0"
                  step="50"
                  value={project.exportSettings.transitionMs}
                  onChange={(event) =>
                    commitProject((draft) => {
                      draft.exportSettings.transitionMs = sanitizeNumber(
                        event.target.value,
                        DEFAULT_TRANSITION_MS
                      );
                    })
                  }
                />
              </label>
            </div>

            <div className="StratsPlannerExportNote">
              <strong>Canvas</strong>
              <span>Locked to 3000 x 3000 px</span>
            </div>

            <label className="StratsPlannerField">
              <span>Export file base name</span>
              <input
                type="text"
                value={project.exportSettings.fileBaseName}
                onChange={(event) =>
                  commitProject((draft) => {
                    draft.exportSettings.fileBaseName = sanitizeFileName(event.target.value);
                  })
                }
              />
            </label>

            <div className="StratsPlannerPanel__divider" />

            <div className="StratsPlannerPanel__eyebrow">Publish to Guide</div>

            {guidePublishRoutes.length === 0 ? (
              <div className="StratsPlannerEmpty">
                No publishable planned guide video slots are registered yet.
              </div>
            ) : (
              <>
                <label className="StratsPlannerField">
                  <span>Guide page</span>
                  <select
                    value={selectedGuidePublishRoutePath}
                    onChange={(event) => setSelectedGuidePublishRoutePath(event.target.value)}
                  >
                    {guidePublishRoutes.map((route) => (
                      <option key={route.routePath} value={route.routePath}>
                        {route.routeTitle}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="StratsPlannerField">
                  <span>Guide section</span>
                  <select
                    value={selectedGuidePublishSectionId}
                    onChange={(event) => setSelectedGuidePublishSectionId(event.target.value)}
                    disabled={!guidePublishSections.length}
                  >
                    {guidePublishSections.map((section) => (
                      <option key={section.sectionId} value={section.sectionId}>
                        {section.sectionLabel}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="StratsPlannerField">
                  <span>Media slot</span>
                  <select
                    value={selectedGuidePublishSlotId}
                    onChange={(event) => setSelectedGuidePublishSlotId(event.target.value)}
                    disabled={!guidePublishSlots.length}
                  >
                    {guidePublishSlots.map((slot) => (
                      <option key={slot.id} value={slot.id}>
                        {slot.mediaLabel}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="StratsPlannerExportNote">
                  <strong>Target Asset Path</strong>
                  <span>
                    {selectedGuidePublishSlot?.plannedAssetPath ||
                      "Choose a guide page, section, and media slot."}
                  </span>
                </div>

                <div className="StratsPlannerExportNote">
                  <strong>Publish Status</strong>
                  <span>
                    {selectedGuidePublishAsset?.remoteUrl ?
                      `Published${
                        selectedGuidePublishAsset.publishedAt ?
                          ` on ${selectedGuidePublishAsset.publishedAt}` :
                          ""
                      }` :
                      "No published guide media yet for this slot."}
                  </span>
                </div>

                {!userData?.uid ? (
                  <div className="StratsPlannerHint">
                    Sign in with an approved admin Firebase account to publish to guide media.
                  </div>
                ) : null}

                <div className="StratsPlannerSequenceActions StratsPlannerSequenceActions--compact">
                  <button
                    type="button"
                    className="StratsPlannerButton"
                    onClick={handlePublishToGuide}
                    disabled={!selectedGuidePublishSlot || !userData?.uid || publishingGuide}
                  >
                    {publishingGuide ? "Publishing..." : "Publish to Guide"}
                  </button>
                </div>
              </>
            )}

              </>
            ) : null}

            <button
              type="button"
              className="StratsPlannerButton StratsPlannerButton--primary"
              onClick={handleExport}
              disabled={exporting}
            >
              {exporting ? "Exporting..." : "Export"}
            </button>
          </div>

          <div className="StratsPlannerPanel StratsPlannerPanel--inspector">
            <div className="StratsPlannerPanel__eyebrow">Image Resize Menu</div>

            {selectedItem ? (
              <>
                <div className="StratsPlannerHint">
                  Selected: {selectedItemMedia?.label || "Unnamed image"}
                </div>
                <div className="StratsPlannerHint">
                  Final canvas size: {Math.round(selectedItem.width)}px x {Math.round(selectedItem.height)}px
                </div>

                <div className="StratsPlannerTwoCol">
                  <label className="StratsPlannerField">
                    <span>X</span>
                    <input
                      type="number"
                      value={Math.round(selectedItem.x)}
                      onChange={(event) =>
                        updateSelectedItem((item) => {
                          item.x = sanitizeNumber(event.target.value, item.x);
                        })
                      }
                    />
                  </label>

                  <label className="StratsPlannerField">
                    <span>Y</span>
                    <input
                      type="number"
                      value={Math.round(selectedItem.y)}
                      onChange={(event) =>
                        updateSelectedItem((item) => {
                          item.y = sanitizeNumber(event.target.value, item.y);
                        })
                      }
                    />
                  </label>
                </div>

                <div className="StratsPlannerTwoCol">
                  <label className="StratsPlannerField">
                    <span>Width (px)</span>
                    <input
                      type="number"
                      min="20"
                      value={Math.round(selectedItem.width)}
                      onChange={(event) =>
                        updateSelectedItem((item) => {
                          item.width = Math.max(20, sanitizeNumber(event.target.value, item.width));
                        })
                      }
                    />
                  </label>

                  <label className="StratsPlannerField">
                    <span>Height (px)</span>
                    <input
                      type="number"
                      min="20"
                      value={Math.round(selectedItem.height)}
                      onChange={(event) =>
                        updateSelectedItem((item) => {
                          item.height = Math.max(20, sanitizeNumber(event.target.value, item.height));
                        })
                      }
                    />
                  </label>
                </div>

                <div className="StratsPlannerTwoCol">
                  <label className="StratsPlannerField">
                    <span>Rotation</span>
                    <input
                      type="number"
                      min="-360"
                      max="360"
                      value={Math.round(selectedItem.rotation)}
                      onChange={(event) =>
                        updateSelectedItem((item) => {
                          item.rotation = normalizeAngle(sanitizeNumber(event.target.value, item.rotation));
                        })
                      }
                    />
                  </label>

                  <label className="StratsPlannerField">
                    <span>Opacity</span>
                    <input
                      type="number"
                      min="0"
                      max="1"
                      step="0.05"
                      value={selectedItem.opacity}
                      onChange={(event) =>
                        updateSelectedItem((item) => {
                          item.opacity = clamp(sanitizeNumber(event.target.value, item.opacity), 0, 1);
                        })
                      }
                    />
                  </label>
                </div>

                <div className="StratsPlannerSequenceActions">
                  <button
                    type="button"
                    className="StratsPlannerButton StratsPlannerButton--ghost"
                    onClick={() =>
                      updateSelectedItem((item) => {
                        item.flipX = !item.flipX;
                      })
                    }
                  >
                    Flip X
                  </button>

                  <button
                    type="button"
                    className="StratsPlannerButton StratsPlannerButton--ghost"
                    onClick={() =>
                      updateSelectedItem((item) => {
                        item.flipY = !item.flipY;
                      })
                    }
                  >
                    Flip Y
                  </button>
                </div>

                <div className="StratsPlannerSequenceActions">
                  <button type="button" className="StratsPlannerButton StratsPlannerButton--ghost" onClick={() => moveLayer("up")}>
                    Layer +
                  </button>
                  <button type="button" className="StratsPlannerButton StratsPlannerButton--ghost" onClick={() => moveLayer("down")}>
                    Layer -
                  </button>
                  <button type="button" className="StratsPlannerButton StratsPlannerButton--ghost" onClick={() => moveLayer("front")}>
                    To front
                  </button>
                  <button type="button" className="StratsPlannerButton StratsPlannerButton--ghost" onClick={() => moveLayer("back")}>
                    To back
                  </button>
                </div>

                <div className="StratsPlannerSequenceActions">
                  <button
                    type="button"
                    className="StratsPlannerButton StratsPlannerButton--danger"
                    onClick={handleDeleteSelectedItem}
                  >
                    Delete
                  </button>
                </div>
              </>
            ) : (
              <div className="StratsPlannerEmpty">
                Select an image on the canvas to edit its exact pixel size, position, rotation, flips, and layer.
              </div>
            )}
          </div>
        </aside>
      </div>

      {toast ? <div className="StratsPlannerToast">{toast}</div> : null}

      <MapSettingsModal
        map={selectedMapForSettings}
        onClose={closeMapSettings}
        onRename={handleMapSettingsRename}
        onReplaceSource={handleMapSettingsSourceReplace}
        onCropSource={handleMapSettingsCrop}
        onMoveEarlier={handleMapSettingsMoveEarlier}
        onMoveLater={handleMapSettingsMoveLater}
        canMoveEarlier={project.maps.findIndex((entry) => entry.id === mapSettingsId) > 0}
        canMoveLater={
          mapSettingsId
            ? project.maps.findIndex((entry) => entry.id === mapSettingsId) < project.maps.length - 1
            : false
        }
        onDelete={handleMapSettingsDelete}
      />

      <AssetSettingsModal
        asset={selectedAsset}
        onClose={() => setAssetSettingsId(null)}
        onRename={handleAssetRename}
        onReplaceSource={handleAssetSourceReplace}
        onDelete={() => handleDeleteAsset(assetSettingsId)}
        onMoveEarlier={handleAssetMoveEarlier}
        onMoveLater={handleAssetMoveLater}
        canMoveEarlier={project.assets.findIndex((entry) => entry.id === assetSettingsId) > 0}
        canMoveLater={
          assetSettingsId
            ? project.assets.findIndex((entry) => entry.id === assetSettingsId) < project.assets.length - 1
            : false
        }
        onAddSubAsset={handleAddSubAsset}
        onRenameSubAsset={handleRenameSubAsset}
        onRemoveSubAsset={handleRemoveSubAsset}
        onReplaceSubAssetSource={handleReplaceSubAssetSource}
      />
    </section>
  );
}
